import { z } from 'zod';
import {
  ENGINE_VERSION,
  TIME_ZONE,
  assertClientFileUploadPolicy,
  canonicalJson,
  type ClientFinancialEntry,
  type ClientFollowUp,
  type ClientContribution,
  type ClientGoal,
  type CalculationSnapshot,
  type Client,
  type ClientPipeline,
  type ClientPipelineStage,
  type FamilyMember,
  type CreateFamilyMemberInput,
  type UpdateFamilyMemberInput,
  type ClientFileLink,
  type CreateClientFileLinkInput,
  type UploadClientFileInput,
  type ClientProtectionNeed,
  type GuaranteeExposure,
  type CreateClient,
  type CreateFollowUpInput,
  type CreateContributionInput,
  type SettleContributionInput,
  createContributionSchema,
  clientContributionSchema,
  settleContributionSchema,
  createGoalSchema,
  goalStatusSchema,
  createFinancialEntrySchema,
  createProtectionNeedSchema,
  createPrivatePensionSimulationSchema,
  privatePensionSimulationSchema,
  assertPrivatePensionSimulationDerived,
  createFinancingSimulationSchema,
  financingSimulationSchema,
  createFinancingComparisonSchema,
  financingComparisonSchema,
  scenarioAssumptionsSchema,
  moneyFromNumber,
  type CreateFinancialEntryInput,
  type CreateFinancingComparisonInput,
  type CreateFinancingSimulationInput,
  type CreateGoalInput,
  type CreatePrivatePensionSimulationInput,
  type CreateProtectionNeedInput,
  type PlanningExclusionSelection,
  type SavePlanningExclusionInput,
  type FinancingComparison,
  type FinancingSimulation,
  type PrivatePensionSimulation,
  type CreateReportSnapshotInput,
  type ReportSnapshot,
  type LegacyReportSnapshot,
  type RetirementCalculationRequest,
  calculationSnapshotSchema,
  calculationSnapshotCalendarConsistent,
  retirementCalculationRequestSchema,
  clientSchema,
  createClientSchema,
  updateClientSchema,
  createFamilyMemberSchema,
  updateFamilyMemberSchema,
  familyMemberSchema,
  clientFinancialEntrySchema,
  guaranteeExposureSchema,
  protectionNeedSchema,
  planningExclusionSchema,
  savePlanningExclusionSchema,
  resolveExcludedTargetIds,
  resolveEffectiveGuaranteeExposures,
  guaranteeExposureBindingMatches,
  protectionRevisionPayload,
  clientPipelineSchema,
  clientGoalSchema,
  clientFileLinkSchema,
  createClientFileLinkSchema,
  createScenarioSchema,
  planScenarioSchema,
  clientFollowUpSchema,
  createFollowUpSchema,
  updateFollowUpSchema,
  createReportSnapshotSchema,
  reportSnapshotSchema,
  legacyReportSnapshotSchema,
  reportsDataSchema,
  auditEntrySchema,
  auditLogSchema,
  type ScenarioAssumptions,
  type UpdateFollowUpInput,
  type UpdateFinancialEntryInput,
} from '@fintracks/contracts';
import {
  annualizeFinancingComparison,
  compareFinancingSchedules,
  consortiumScheduleRowsFromInput,
  periodicInternalRateOfReturnPct,
  presentValueSchedule,
  privatePensionProjection,
  sacSchedule,
} from '@fintracks/calculation-engine';
import { reportSummaryForClient } from '@fintracks/directus-sdk';
import type { CreateScenarioInput, FintracksRepository, PlanScenario, SessionUser } from '@fintracks/directus-sdk';
import { DEMO_CLIENT_ID, DEMO_SCENARIO_ID, demoAssumptions, demoClients, demoClientFiles, demoFamilyMembers, demoFinancials, demoGuaranteeExposures, demoFinancingSimulations, demoFollowUp, demoGoals, demoPensionSimulations, demoProtection } from './demo';
import { runRetirementSimulation, serializeRetirementResult, sumMoney } from '../lib/finance';
import { calculateProtectionPlan, protectionDependentsFromFamily } from '../lib/protection';
import { calculateGoalFunding } from '../lib/goals';
import { reportSourceRevisionDigest } from '../lib/reportFingerprint';
import { clearPendingOperationDomain, loadPendingOperationEvidence, loadPendingOperations } from '../lib/operationSafety';
const CLIENTS_KEY = 'fintracks.demo.clients.v1';
const SESSION_KEY = 'fintracks.demo.session.v1';
const SCENARIO_KEY = 'fintracks.demo.scenarios.v1';
const SNAPSHOTS_KEY = 'fintracks.demo.snapshots.v2';
const GOALS_KEY = 'fintracks.demo.goals.v1';
const FOLLOW_UP_KEY = 'fintracks.demo.follow-up.v1';
const CONTRIBUTIONS_KEY = 'fintracks.demo.contributions.v1';
const MAX_CONTRIBUTIONS_PER_CLIENT = 5_000;
const FINANCIALS_KEY = 'fintracks.demo.financials.v1';
const GUARANTEE_EXPOSURES_KEY = 'fintracks.demo.guarantee-exposures.v1';
const PROTECTION_KEY = 'fintracks.demo.protection.v1';
const REPORTS_KEY = 'fintracks.demo.reports.v1';
const LEGACY_REPORTS_KEY = 'fintracks.demo.reports-legacy.v1';
const PENSIONS_KEY = 'fintracks.demo.pensions.v2';
const FINANCING_KEY = 'fintracks.demo.financing.v1';
const COMPARISONS_KEY = 'fintracks.demo.comparisons.v1';
const SETTINGS_KEY = 'fintracks.demo.settings.v1';
const FAMILY_KEY = 'fintracks.demo.family.v1';
const EXCLUSIONS_KEY = 'fintracks.demo.exclusions.v1';
const FILES_KEY = 'fintracks.demo.files.v1';
const FILE_BLOBS_KEY = 'fintracks.demo.file-blobs.v1';
const PIPELINE_KEY = 'fintracks.demo.pipeline.v1';
const AUDIT_KEY = 'fintracks.demo.audit.v1';
const CONTRIBUTION_IDEMPOTENCY_KEY = 'fintracks.demo.contribution-idempotency.v1';
const CONTRIBUTION_LEDGER_KEY = 'fintracks.demo.contribution-ledger.v2';
const OPERATION_RECEIPTS_KEY = 'fintracks.demo.operation-receipts.v1';
const canonicalDate = (value: Date | string) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(value)).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
};
const canonicalToday = () => canonicalDate(new Date());
function syntheticDemoBlob(file: ClientFileLink): Blob {
  const signature: Record<string, number[]> = {
    'application/pdf': [0x25, 0x50, 0x44, 0x46, 0x2d],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [0x50, 0x4b, 0x03, 0x04],
  };
  const seed = new TextEncoder().encode(`Fintracks synthetic fixture ${file.id}\n`);
  const prefix = signature[file.mimeType] ?? [];
  const bytes = new Uint8Array(file.sizeBytes);
  for (let index = 0; index < bytes.length; index += 1) bytes[index] = prefix[index] ?? seed[(index - prefix.length) % seed.length]!;
  return new Blob([bytes], { type: file.mimeType });
}
const demoFileBlobs = new Map<string, Blob>(demoClientFiles.map((file) => [file.id, syntheticDemoBlob(file)]));

type PersistedDemoBlob = { mimeType: string; data: string };

function encodeDemoBlob(bytes: Uint8Array): string {
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 0x8000) binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  return btoa(binary);
}

function decodeDemoBlob(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

async function readDemoBlobBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') return new Uint8Array(await blob.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Não foi possível ler o arquivo demo'));
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.readAsArrayBuffer(blob);
  });
}

async function sha256Bytes(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as unknown as BufferSource);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function fileUploadEvidenceForContentHash(input: UploadClientFileInput, actualMime: string, contentHash: string) {
  const requestPayload = { clientId: input.clientId, name: input.name.trim(), category: input.category.trim(), mimeType: actualMime, sizeBytes: input.file.size, contentHash };
  const requestHash = await sha256({ kind: 'file.upload', input: requestPayload });
  const key = (input.idempotencyKey?.trim() || `file-upload-${requestHash}`).toLowerCase();
  return { kind: 'file.upload' as const, key, requestHash };
}

export async function buildDemoFileUploadEvidence(input: UploadClientFileInput) {
  const actualMime = input.file.type || 'application/octet-stream';
  const bytes = await readDemoBlobBytes(input.file);
  return fileUploadEvidenceForContentHash(input, actualMime, await sha256Bytes(bytes));
}

async function followUpEvidenceForValidated(validated: CreateFollowUpInput) {
  const requestPayload = { clientId: validated.clientId, type: validated.type, title: validated.title.trim(), description: validated.description.trim(), dueAt: validated.dueAt ?? null, assignedTo: validated.assignedTo ?? null };
  const requestHash = await sha256({ kind: 'followup.create', input: requestPayload });
  return { kind: 'followup.create' as const, key: requestHash, requestHash };
}

export async function buildDemoFollowUpEvidence(input: CreateFollowUpInput) {
  return followUpEvidenceForValidated(createFollowUpSchema.parse(input));
}

function demoFileSignatureMatches(mimeType: string, bytes: Uint8Array): boolean {
  const starts = (prefix: readonly number[]) => prefix.every((value, index) => bytes[index] === value);
  if (mimeType === 'application/pdf') return new TextDecoder().decode(bytes.slice(0, 5)) === '%PDF-';
  if (mimeType === 'image/png') return starts([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
  if (mimeType === 'image/jpeg') return starts([0xff, 0xd8, 0xff]);
  if (mimeType === 'image/webp') return new TextDecoder().decode(bytes.slice(0, 4)) === 'RIFF' && new TextDecoder().decode(bytes.slice(8, 12)) === 'WEBP';
  if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return starts([0x50, 0x4b, 0x03, 0x04]);
  if (mimeType === 'text/csv') return bytes.length > 0 && !bytes.includes(0);
  return false;
}

function assertDemoFileBytes(file: Pick<ClientFileLink, 'mimeType' | 'sizeBytes'>, bytes: Uint8Array): void {
  if (bytes.byteLength !== file.sizeBytes) throw new Error('O conteúdo do arquivo demo diverge do tamanho declarado');
  if (!demoFileSignatureMatches(file.mimeType, bytes)) throw new Error('A assinatura do arquivo demo não corresponde ao MIME declarado');
}

async function assertDemoFileIntegrity(file: Pick<ClientFileLink, 'mimeType' | 'sizeBytes' | 'contentHash'>, bytes: Uint8Array): Promise<void> {
  assertDemoFileBytes(file, bytes);
  if (file.contentHash && await sha256Bytes(bytes) !== file.contentHash) throw new Error('O conteúdo do arquivo demo diverge do hash persistido');
}

async function fileUploadRequestHash(file: Pick<ClientFileLink, 'clientId' | 'name' | 'category' | 'mimeType' | 'sizeBytes' | 'contentHash'>): Promise<string> {
  if (!file.contentHash) throw new Error('O arquivo não possui contentHash para rederivar o requestHash');
  return sha256({ kind: 'file.upload', input: { clientId: file.clientId, name: file.name.trim(), category: file.category.trim(), mimeType: file.mimeType, sizeBytes: file.sizeBytes, contentHash: file.contentHash } });
}

async function followUpRequestHash(item: Pick<ClientFollowUp, 'clientId' | 'type' | 'title' | 'description' | 'dueAt' | 'assignedTo'>): Promise<string> {
  return sha256({ kind: 'followup.create', input: { clientId: item.clientId, type: item.type, title: item.title.trim(), description: item.description.trim(), dueAt: item.dueAt ?? null, assignedTo: item.assignedTo ?? null } });
}

function persistedDemoBlobs(): Record<string, PersistedDemoBlob> {
  const raw = read<Record<string, unknown>>(demoStorage, FILE_BLOBS_KEY, {});
  const parsed: Record<string, PersistedDemoBlob> = {};
  for (const [id, value] of Object.entries(raw)) {
    const record = value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
    if (!record || typeof record.mimeType !== 'string' || typeof record.data !== 'string') throw new Error(`Armazenamento ${FILE_BLOBS_KEY}:${id} inválido`);
    parsed[id] = { mimeType: record.mimeType, data: record.data };
  }
  return parsed;
}

function savePersistedDemoBlobs(blobs: Record<string, PersistedDemoBlob>): void {
  setDemoItem(FILE_BLOBS_KEY, JSON.stringify(blobs));
}

const DEMO_PLAN_ID = '77777777-7777-4777-8777-777777777777';
const DEMO_ACTOR_ID = '00000000-0000-4000-8000-000000000099';
const UUID_TEXT = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export interface DemoSession extends SessionUser {
  name: string;
  role: { id: string; name: string };
}

function readDemoSession(): DemoSession | null {
  const saved = sessionStorage.getItem(SESSION_KEY);
  if (!saved) return null;
  try {
    const value = JSON.parse(saved) as Record<string, unknown>;
    if (!value || typeof value.id !== 'string' || typeof value.email !== 'string' || !value.role || typeof value.role !== 'object') throw new Error('invalid session');
    return value as unknown as DemoSession;
  } catch {
    try { sessionStorage.removeItem(SESSION_KEY); } catch { /* storage indisponível: o chamador ainda recebe sessão anônima */ }
    return null;
  }
}

export interface DemoSettings {
  realValues: boolean;
  compactTables: boolean;
  reviewReminderDays: number;
}
const demoSettingsSchema = z.strictObject({ realValues: z.boolean(), compactTables: z.boolean(), reviewReminderDays: z.number().int().min(15).max(365) });
const defaultDemoSettings: DemoSettings = { realValues: true, compactTables: false, reviewReminderDays: 90 };

interface DemoContributionIdempotency {
  key: string;
  kind: 'create' | 'settle';
  clientId: string;
  contributionId: string;
  requestHash: string;
}

interface DemoOperationReceipt {
  key: string;
  kind: 'followup.create' | 'file.upload';
  clientId: string;
  entityId: string;
  requestHash: string;
}

const demoOperationReceiptSchema = z.strictObject({
  key: z.string().min(8).max(200).regex(/^[\x21-\x7e]+$/),
  kind: z.enum(['followup.create', 'file.upload']),
  clientId: z.string().uuid(),
  entityId: z.string().uuid(),
  requestHash: z.string().regex(/^[a-f0-9]{64}$/),
});

const demoContributionIdempotencySchema = z.strictObject({
  key: z.string().min(8).max(200).regex(/^[\x21-\x7e]+$/),
  kind: z.enum(['create', 'settle']),
  clientId: z.string().uuid(),
  contributionId: z.string().uuid(),
  requestHash: z.string().regex(/^[a-f0-9]{64}$/),
});

interface DemoContributionLedger {
  version: 2;
  contributions: ClientContribution[];
  idempotency: DemoContributionIdempotency[];
}

function demoStorageKey(key: string, sessionId?: string): string {
  const currentSession = readDemoSession();
  return `${key}:${sessionId ?? currentSession?.id ?? 'anonymous'}`;
}

const demoStorage = {
  getItem: (key: string) => localStorage.getItem(demoStorageKey(key)),
  setItem: (key: string, value: string) => setDemoItem(key, value),
  removeItem: (key: string) => localStorage.removeItem(demoStorageKey(key)),
};

const DEMO_STORAGE_QUOTA_MESSAGE = 'Armazenamento local do modo demo sem espaço; reduza o arquivo ou limpe a sessão.';
function isStorageQuotaError(error: unknown): boolean {
  const name = error && typeof error === 'object' && 'name' in error ? String((error as { name?: unknown }).name) : '';
  return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED';
}

function setDemoItem(key: string, value: string): void {
  try {
    localStorage.setItem(demoStorageKey(key), value);
  } catch (error) {
    if (isStorageQuotaError(error)) throw new Error(DEMO_STORAGE_QUOTA_MESSAGE);
    throw error;
  }
}

type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function read<T>(storage: KeyValueStorage, key: string, fallback: T): T {
  const saved = storage.getItem(key);
  if (!saved) return fallback;
  try { return JSON.parse(saved) as T; } catch { throw new Error(`Armazenamento ${key} corrompido`); }
}

function readStrictArray<T>(storage: Pick<Storage, 'getItem' | 'setItem'>, key: string, fallback: T[], parse: (value: unknown) => T): T[] {
  let candidate: unknown = fallback;
  const saved = storage.getItem(key);
  if (saved) {
    try { candidate = JSON.parse(saved); } catch { throw new Error(`Armazenamento ${key} corrompido`); }
  }
  if (!Array.isArray(candidate)) throw new Error(`Armazenamento ${key} inválido`);
  const parsed = candidate.map((item, index) => {
    try { return parse(item); } catch { throw new Error(`Registro ${key}[${index}] inválido`); }
  });
  if (!saved) storage.setItem(key, JSON.stringify(parsed));
  return parsed;
}

function readStrictMap<T>(storage: Pick<Storage, 'getItem' | 'setItem'>, key: string, fallback: Record<string, T>, parse: (value: unknown) => T): Record<string, T> {
  let candidate: unknown = fallback;
  const saved = storage.getItem(key);
  if (saved) {
    try { candidate = JSON.parse(saved); } catch { throw new Error(`Armazenamento ${key} corrompido`); }
  }
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) throw new Error(`Armazenamento ${key} inválido`);
  const parsed: Record<string, T> = {};
  for (const [entryKey, value] of Object.entries(candidate as Record<string, unknown>)) {
    try { parsed[entryKey] = parse(value); } catch { throw new Error(`Registro ${key}:${entryKey} inválido`); }
  }
  if (!saved) storage.setItem(key, JSON.stringify(parsed));
  return parsed;
}

function persistedMoneyNumber(value: string): number {
  const match = /^(\d+)\.(\d{2})$/.exec(value);
  if (!match) throw new Error('Valor monetário persistido inválido');
  const cents = BigInt(match[1]!) * 100n + BigInt(match[2]!);
  if (cents > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error('Valor monetário persistido excede o limite seguro');
  const parsed = Number(cents) / 100;
  if (moneyFromNumber(parsed) !== value) throw new Error('Valor monetário persistido não faz round-trip exato');
  return parsed;
}

function sumPersistedMoney(values: readonly string[]): string {
  const cents = values.reduce((total, value) => {
    const match = /^(\d+)\.(\d{2})$/.exec(value);
    if (!match) throw new Error('Valor monetário persistido inválido');
    return total + BigInt(match[1]!) * 100n + BigInt(match[2]!);
  }, 0n);
  return `${cents / 100n}.${String(cents % 100n).padStart(2, '0')}`;
}

function assertDemoPensionDerived(simulation: PrivatePensionSimulation): void {
  assertPrivatePensionSimulationDerived(simulation);
  const projection = privatePensionProjection({
    product: simulation.product, taxRegime: simulation.taxRegime, socialSecurity: simulation.socialSecurity,
    annualTaxableIncome: persistedMoneyNumber(simulation.annualTaxableIncome), annualDeductibleExpenses: persistedMoneyNumber(simulation.annualDeductibleExpenses),
    currentReserve: persistedMoneyNumber(simulation.currentReserve), monthlyContribution: persistedMoneyNumber(simulation.monthlyContribution),
    annualReturnPct: simulation.annualReturnPct, inflationAnnualPct: simulation.inflationAnnualPct,
    accumulationYears: simulation.accumulationYears, retirementIncomeYears: simulation.retirementIncomeYears,
  });
  const expected = {
    suggestedPgblAnnual: projection.suggestedPgblAnnual.toFixed(2), taxBenefit: projection.taxBenefit.toFixed(2), grossReserve: projection.grossReserve.toFixed(2),
    taxableRescue: projection.taxableRescue.toFixed(2), estimatedTax: projection.estimatedTax.toFixed(2), netReserve: projection.netReserve.toFixed(2),
    sustainableMonthlyIncome: projection.sustainableMonthlyIncome.toFixed(2), taxRatePct: projection.taxRatePct,
  };
  if (canonicalJson(simulation.result) !== canonicalJson(expected)) throw new Error('Resultado de previdência demo não corresponde ao motor canônico');
}

function assertDemoProtectionDerived(value: ClientProtectionNeed): void {
  const expected = calculateProtectionPlan({
    clientId: value.clientId, monthlyLivingExpenses: persistedMoneyNumber(value.monthlyLivingExpenses), horizonYears: value.horizonYears,
    dependents: value.dependents, estateBaseValue: persistedMoneyNumber(value.estateBaseValue), legalFeesPct: value.legalFeesPct,
    courtCostsPct: value.courtCostsPct, inheritanceTaxPct: value.inheritanceTaxPct, emergencyMonths: value.emergencyMonths,
    currentGuarantees: persistedMoneyNumber(value.currentGuarantees), wholeLifeInsuredCapital: persistedMoneyNumber(value.wholeLifeInsuredCapital),
    deductiblePrivatePension: persistedMoneyNumber(value.deductiblePrivatePension), realAnnualRatePct: value.realAnnualRatePct,
  } as CreateProtectionNeedInput, { ...value, version: value.version - 1 });
  for (const field of ['totalExpenses', 'emergencyReserve', 'successionNeed', 'recommendedCapital', 'coverageGap'] as const) {
    if (value[field] !== expected[field]) throw new Error('Derivados de proteção demo não correspondem ao motor canônico');
  }
}

function assertDemoFinancingDerived(simulation: FinancingSimulation): void {
  const expectedSchedule = simulation.kind === 'sac'
    ? sacSchedule(persistedMoneyNumber(simulation.principal), simulation.termMonths, simulation.annualInterestPct, simulation.annualIndexationPct)
      .map((row) => ({ installment: row.number, payment: row.payment.toFixed(2), principal: row.amortization.toFixed(2), interest: row.interest.toFixed(2), balance: row.closingBalance.toFixed(2) }))
    : consortiumScheduleRowsFromInput({
      credit: persistedMoneyNumber(simulation.principal), termMonths: simulation.termMonths, anticipationFeePct: simulation.anticipationFeePct,
      administrationFeePct: simulation.administrationFeePct, reserveFundPct: simulation.reserveFundPct, monthlyInsurancePct: simulation.monthlyInsurancePct,
      installmentReducerPct: simulation.installmentReducerPct, contemplationInstallment: simulation.contemplationInstallment,
      totalBidPct: simulation.totalBidPct, embeddedBidPct: simulation.embeddedBidPct, annualAdjustmentPct: simulation.annualAdjustmentPct,
    }).map((row, index) => ({ installment: index + 1, payment: row.payment.toFixed(2), principal: row.principal.toFixed(2), interest: row.interest.toFixed(2), balance: row.balance.toFixed(2) }));
  const expectedTotal = sumPersistedMoney(expectedSchedule.map((row) => row.payment));
  if (canonicalJson(simulation.schedule) !== canonicalJson(expectedSchedule) || simulation.firstPayment !== expectedSchedule[0]?.payment || simulation.lastPayment !== expectedSchedule.at(-1)?.payment || simulation.totalPaid !== expectedTotal) {
    throw new Error('Cronograma financeiro demo não corresponde ao motor canônico');
  }
}

function assertDemoComparisonDerived(comparison: FinancingComparison, simulations: readonly FinancingSimulation[]): void {
  const suggested = simulations.find((item) => item.id === comparison.suggestedSimulationId);
  const alternative = simulations.find((item) => item.id === comparison.alternativeSimulationId);
  if (!suggested || !alternative || suggested.clientId !== comparison.clientId || alternative.clientId !== comparison.clientId) throw new Error('Comparação financeira demo referencia simulação inválida');
  const suggestedPayments = suggested.schedule.map((row) => persistedMoneyNumber(row.payment));
  const alternativePayments = alternative.schedule.map((row) => persistedMoneyNumber(row.payment));
  const rows = compareFinancingSchedules(suggestedPayments, alternativePayments, comparison.annualOpportunityRatePct);
  const annualRows = annualizeFinancingComparison(rows);
  const tirAnnualPct = periodicInternalRateOfReturnPct([0, ...rows.map((row) => row.difference.toNumber())]);
  const expected = {
    suggestedPresentValue: presentValueSchedule(suggestedPayments, comparison.annualOpportunityRatePct).toFixed(2),
    alternativePresentValue: presentValueSchedule(alternativePayments, comparison.annualOpportunityRatePct).toFixed(2),
    tirAnnualPct,
    rows: rows.map((row) => ({ installment: row.installment, suggestedPayment: row.suggestedPayment.toFixed(2), alternativePayment: row.alternativePayment.toFixed(2), difference: row.difference.toFixed(2), accumulatedDifference: row.accumulatedDifference.toFixed(2) })),
    annualRows: annualRows.map((row) => ({ year: row.year, suggestedPayment: row.suggestedPayment.toFixed(2), alternativePayment: row.alternativePayment.toFixed(2), difference: row.difference.toFixed(2), accumulatedDifference: row.accumulatedDifference.toFixed(2) })),
  };
  const actual = { suggestedPresentValue: comparison.suggestedPresentValue, alternativePresentValue: comparison.alternativePresentValue, tirAnnualPct: comparison.tirAnnualPct, rows: comparison.rows, annualRows: comparison.annualRows };
  if (canonicalJson(actual) !== canonicalJson(expected)) throw new Error('Comparação financeira demo não corresponde ao motor canônico');
}

function clientCompleteness(client: Pick<Client, 'fullName' | 'shortName' | 'birthDate' | 'statedAge' | 'profession' | 'email' | 'phone'>): number {
  const filled = [client.fullName, client.shortName, client.birthDate ?? client.statedAge, client.profession, client.email, client.phone]
    .filter((value) => value !== null && value !== '').length;
  return Math.round(filled / 6 * 100);
}

function appendAudit(clientId: string, action: string, entityId: string, fields?: string[]) {
  const store = read<Record<string, unknown>>(demoStorage, AUDIT_KEY, {});
  const existing = store[clientId];
  const existingRecord = existing && typeof existing === 'object' && !Array.isArray(existing) ? existing as Record<string, unknown> : null;
  const legacyEntries = Array.isArray(existing) ? existing : existingRecord && Array.isArray(existingRecord.entries) ? existingRecord.entries : [];
  const existingLog = existing && typeof existing === 'object' && !Array.isArray(existing) ? auditLogSchema.safeParse(existing) : null;
  const normalizeEntry = (entry: unknown, index: number) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) throw new Error(`Registro ${AUDIT_KEY}:${clientId}[${index}] inválido`);
    const raw = entry as Record<string, unknown>;
    const actorId = typeof raw.actorId === 'string' && UUID_TEXT.test(raw.actorId) ? raw.actorId : DEMO_ACTOR_ID;
    const parsed = auditEntrySchema.safeParse({ ...raw, actorId });
    if (!parsed.success) throw new Error(`Registro ${AUDIT_KEY}:${clientId}[${index}] inválido`);
    return parsed.data;
  };
  const entries = existingLog?.success ? existingLog.data.entries : legacyEntries.map(normalizeEntry);
  const sessionActor = readDemoSession()?.id;
  const actorId = sessionActor && UUID_TEXT.test(sessionActor) ? sessionActor : DEMO_ACTOR_ID;
  const entry = auditEntrySchema.parse({ id: crypto.randomUUID(), action, entityId, at: new Date().toISOString(), actorId, ...(fields && fields.length > 0 ? { fields } : {}) });
  store[clientId] = auditLogSchema.parse({ version: existingLog?.success ? existingLog.data.version : (existingRecord?.version ?? 1), entries: [...entries, entry] });
  demoStorage.setItem(AUDIT_KEY, JSON.stringify(store));
}

function clients(): Client[] {
  return readStrictArray(demoStorage, CLIENTS_KEY, demoClients, (item) => clientSchema.parse(item));
}

function scenarioStore(): Record<string, PlanScenario[]> {
  const raw = read<Record<string, unknown>>(demoStorage, SCENARIO_KEY, {});
  const store: Record<string, PlanScenario[]> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (Array.isArray(value)) {
      store[key] = value.map((item) => {
        if (!item || typeof item !== 'object' || Array.isArray(item)) return item as PlanScenario;
        const record = item as Record<string, unknown>;
        const legacyPlanId = typeof record.planId === 'string' && record.planId.startsWith('demo-plan-');
        const normalized = {
          ...record,
          ...(legacyPlanId ? { planId: key === DEMO_CLIENT_ID ? DEMO_PLAN_ID : crypto.randomUUID() } : {}),
          assumptionsVersion: record.assumptionsVersion ?? 1,
        };
        return planScenarioSchema.parse(normalized);
      });
      continue;
    }
    if (value && typeof value === 'object' && 'baseYear' in value) {
      store[DEMO_CLIENT_ID] = [...(store[DEMO_CLIENT_ID] ?? []), {
        id: key,
        planId: DEMO_PLAN_ID,
        name: 'Cenário base',
        isPrimary: store[DEMO_CLIENT_ID]?.length === 0,
        visibleInComparison: true,
        version: 1,
        assumptionsVersion: 1,
        assumptions: value as ScenarioAssumptions,
      }];
    }
  }
  return store;
}

function saveScenarioStore(store: Record<string, PlanScenario[]>): void {
  demoStorage.setItem(SCENARIO_KEY, JSON.stringify(store));
}

function goalStore(): Record<string, ClientGoal[]> {
  const fallback = { [DEMO_CLIENT_ID]: demoGoals };
  const raw = read<Record<string, unknown>>(demoStorage, GOALS_KEY, fallback);
  const store: Record<string, ClientGoal[]> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!Array.isArray(value)) throw new Error(`Armazenamento ${GOALS_KEY}:${key} inválido`);
    store[key] = value.map((item, index) => {
      try { return clientGoalSchema.parse(item); } catch { throw new Error(`Registro ${GOALS_KEY}:${key}[${index}] inválido`); }
    });
  }
  if (!demoStorage.getItem(GOALS_KEY)) demoStorage.setItem(GOALS_KEY, JSON.stringify(store));
  return store;
}

function saveGoalStore(store: Record<string, ClientGoal[]>): void {
  demoStorage.setItem(GOALS_KEY, JSON.stringify(store));
}

function followUpStore(): ClientFollowUp[] {
  return readStrictArray(demoStorage, FOLLOW_UP_KEY, demoFollowUp, (item) => clientFollowUpSchema.parse(item));
}

function saveFollowUpStore(items: ClientFollowUp[]): void {
  demoStorage.setItem(FOLLOW_UP_KEY, JSON.stringify(items));
}

function operationReceiptStore(): DemoOperationReceipt[] {
  return readStrictArray(demoStorage, OPERATION_RECEIPTS_KEY, [], (item) => demoOperationReceiptSchema.parse(item));
}

function saveOperationReceiptStore(items: DemoOperationReceipt[]): void {
  demoStorage.setItem(OPERATION_RECEIPTS_KEY, JSON.stringify(items));
}

function parseContributionLedger(value: unknown, key: string): DemoContributionLedger {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`Armazenamento ${key} inválido`);
  const record = value as Record<string, unknown>;
  if (record.version !== 2 || !Array.isArray(record.contributions) || !Array.isArray(record.idempotency)) throw new Error(`Armazenamento ${key} inválido`);
  const contributions = record.contributions.map((item, index) => {
    try { return clientContributionSchema.parse(item); } catch { throw new Error(`Registro ${key}.contributions[${index}] inválido`); }
  });
  const idempotency = record.idempotency.map((item, index) => {
    try { return demoContributionIdempotencySchema.parse(item); } catch { throw new Error(`Registro ${key}.idempotency[${index}] inválido`); }
  });
  const contributionIds = new Set(contributions.map((item) => item.id));
  if (contributionIds.size !== contributions.length) throw new Error(`Registro ${key}.contributions contém ID duplicado`);
  const idempotencyKeys = new Set<string>();
  const normalizedIdempotency = idempotency.map((entry) => {
    if (!contributionIds.has(entry.contributionId)) throw new Error(`Registro ${key}.idempotency referencia aporte inexistente`);
    const normalizedKey = entry.key.trim().toLowerCase();
    if (idempotencyKeys.has(normalizedKey)) throw new Error(`Registro ${key}.idempotency contém chave duplicada`);
    idempotencyKeys.add(normalizedKey);
    const contribution = contributions.find((item) => item.id === entry.contributionId);
    if (!contribution || contribution.clientId !== entry.clientId) throw new Error(`Registro ${key}.idempotency possui cliente divergente`);
    return { ...entry, key: normalizedKey };
  });
  return { version: 2, contributions, idempotency: normalizedIdempotency };
}

function parseContributionArray(value: unknown, key: string): ClientContribution[] {
  if (!Array.isArray(value)) throw new Error(`Armazenamento ${key} inválido`);
  return value.map((item, index) => {
    try { return clientContributionSchema.parse(item); } catch { throw new Error(`Registro ${key}[${index}] inválido`); }
  });
}

function parseContributionIdempotencyArray(value: unknown, key: string): DemoContributionIdempotency[] {
  if (!Array.isArray(value)) throw new Error(`Armazenamento ${key} inválido`);
  const seen = new Set<string>();
  return value.map((item, index) => {
    try {
      const parsed = demoContributionIdempotencySchema.parse(item);
      const normalizedKey = parsed.key.trim().toLowerCase();
      if (seen.has(normalizedKey)) throw new Error('chave duplicada');
      seen.add(normalizedKey);
      return { ...parsed, key: normalizedKey };
    } catch { throw new Error(`Registro ${key}[${index}] inválido`); }
  });
}

function contributionLedger(): DemoContributionLedger {
  const stored = read<unknown>(demoStorage, CONTRIBUTION_LEDGER_KEY, null);
  if (stored !== null) return parseContributionLedger(stored, CONTRIBUTION_LEDGER_KEY);
  const migrated = parseContributionLedger({
    version: 2,
    contributions: parseContributionArray(read<unknown>(demoStorage, CONTRIBUTIONS_KEY, []), CONTRIBUTIONS_KEY),
    idempotency: parseContributionIdempotencyArray(read<unknown>(demoStorage, CONTRIBUTION_IDEMPOTENCY_KEY, []), CONTRIBUTION_IDEMPOTENCY_KEY),
  }, CONTRIBUTION_LEDGER_KEY);
  demoStorage.setItem(CONTRIBUTION_LEDGER_KEY, JSON.stringify(migrated));
  return migrated;
}

function saveContributionLedger(ledger: DemoContributionLedger): void {
  demoStorage.setItem(CONTRIBUTION_LEDGER_KEY, JSON.stringify(ledger));
}

function contributionStore(): ClientContribution[] {
  return contributionLedger().contributions;
}

function compareContributionsCanonical(left: ClientContribution, right: ClientContribution): number {
  return right.dueDate.localeCompare(left.dueDate) || left.id.localeCompare(right.id);
}

function contributionIdempotencyStore(): DemoContributionIdempotency[] {
  return contributionLedger().idempotency;
}

function requireDemoContributionKey(value: string): string {
  const key = typeof value === 'string' ? value.trim() : '';
  if (key.length < 8 || key.length > 200 || !/^[\x21-\x7e]+$/.test(key)) {
    throw new Error('Idempotency-Key deve ter entre 8 e 200 caracteres imprimíveis');
  }
  return key.toLowerCase();
}

function createContributionReceipt(item: ClientContribution): ClientContribution {
  return {
    ...item,
    actualAmount: null,
    status: 'pending',
    realizedAt: null,
    settlementNote: null,
    version: 1,
    updatedAt: item.createdAt,
  };
}

function financialStore(): ClientFinancialEntry[] {
  return readStrictArray(demoStorage, FINANCIALS_KEY, demoFinancials, (item) => clientFinancialEntrySchema.parse(item));
}

function saveFinancialStore(items: ClientFinancialEntry[]): void {
  demoStorage.setItem(FINANCIALS_KEY, JSON.stringify(items));
}

function guaranteeExposureStore(): GuaranteeExposure[] {
  const items = readStrictArray(demoStorage, GUARANTEE_EXPOSURES_KEY, demoGuaranteeExposures, (item) => guaranteeExposureSchema.parse(item));
  const ids = new Set<string>();
  for (const item of items) {
    if (ids.has(item.id)) throw new Error('Exposições de garantia demo duplicadas.');
    ids.add(item.id);
  }
  return items;
}

function protectionStore(): Record<string, ClientProtectionNeed> {
  return readStrictMap(demoStorage, PROTECTION_KEY, { [DEMO_CLIENT_ID]: demoProtection }, (item) => {
    const parsed = protectionNeedSchema.parse(item);
    assertDemoProtectionDerived(parsed);
    return parsed;
  });
}

function saveProtectionStore(store: Record<string, ClientProtectionNeed>): void {
  demoStorage.setItem(PROTECTION_KEY, JSON.stringify(store));
}

function reportStore(): ReportSnapshot[] {
  const raw = read<unknown>(demoStorage, REPORTS_KEY, []);
  if (!Array.isArray(raw)) throw new Error(`Armazenamento ${REPORTS_KEY} inválido`);
  const canonical: ReportSnapshot[] = [];
  const legacy: LegacyReportSnapshot[] = readStrictArray(demoStorage, LEGACY_REPORTS_KEY, [], (item) => legacyReportSnapshotSchema.parse(item));
  raw.forEach((item, index) => {
    const parsed = reportSnapshotSchema.safeParse(item);
    const details = parsed.success && parsed.data.details && typeof parsed.data.details === 'object' && !Array.isArray(parsed.data.details) ? parsed.data.details as Record<string, unknown> : null;
    if (!parsed.success) {
      const legacyParsed = legacyReportSnapshotSchema.safeParse(item);
      if (!legacyParsed.success || (item && typeof item === 'object' && !Array.isArray(item) && (item as Record<string, unknown>).canonical === true)) throw new Error(`Registro ${REPORTS_KEY}[${index}] inválido`);
      legacy.push(legacyParsed.data);
    } else if (Object.prototype.hasOwnProperty.call(details ?? {}, 'family')) canonical.push(parsed.data);
    else legacy.push(legacyReportSnapshotSchema.parse(item));
  });
  const envelope = reportsDataSchema.parse({ version: 1, reports: canonical, legacyReports: legacy });
  if (legacy.length > 0 || !demoStorage.getItem(REPORTS_KEY)) {
    demoStorage.setItem(LEGACY_REPORTS_KEY, JSON.stringify(envelope.legacyReports));
    demoStorage.setItem(REPORTS_KEY, JSON.stringify(envelope.reports));
  }
  if (envelope.reports.some((report) => report.clientId !== DEMO_CLIENT_ID) || envelope.legacyReports.some((report) => report.clientId !== DEMO_CLIENT_ID)) throw new Error('Relatório demo fora do namespace do cliente');
  return envelope.reports;
}

function saveReportStore(reports: ReportSnapshot[]): void {
  const legacy = readStrictArray(demoStorage, LEGACY_REPORTS_KEY, [], (item) => legacyReportSnapshotSchema.parse(item));
  const envelope = reportsDataSchema.parse({ version: 1, reports, legacyReports: legacy });
  if (envelope.reports.some((report) => report.clientId !== DEMO_CLIENT_ID) || envelope.legacyReports.some((report) => report.clientId !== DEMO_CLIENT_ID)) throw new Error('Relatório demo fora do namespace do cliente');
  demoStorage.setItem(REPORTS_KEY, JSON.stringify(envelope.reports));
  demoStorage.setItem(LEGACY_REPORTS_KEY, JSON.stringify(envelope.legacyReports));
}

function legacyReportStore(): LegacyReportSnapshot[] {
  reportStore();
  return readStrictArray(demoStorage, LEGACY_REPORTS_KEY, [], (item) => legacyReportSnapshotSchema.parse(item));
}

function calculationStore(): CalculationSnapshot[] {
  return readStrictArray(demoStorage, SNAPSHOTS_KEY, [], (item) => {
    const snapshot = calculationSnapshotSchema.parse(item);
    if (!calculationSnapshotCalendarConsistent(snapshot.input, snapshot.output)) throw new Error('Snapshot de cálculo demo incoerente com o calendário');
    return snapshot;
  });
}

function pensionStore(): Record<string, PrivatePensionSimulation[]> {
  const raw = read<Record<string, unknown>>(demoStorage, PENSIONS_KEY, { [DEMO_CLIENT_ID]: demoPensionSimulations });
  const store: Record<string, PrivatePensionSimulation[]> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!Array.isArray(value)) throw new Error(`Armazenamento ${PENSIONS_KEY}:${key} inválido`);
    store[key] = value.map((item, index) => {
      try {
        const parsed = privatePensionSimulationSchema.parse(item);
        assertDemoPensionDerived(parsed);
        return parsed;
      } catch { throw new Error(`Registro ${PENSIONS_KEY}:${key}[${index}] inválido`); }
    });
  }
  if (!demoStorage.getItem(PENSIONS_KEY)) demoStorage.setItem(PENSIONS_KEY, JSON.stringify(store));
  return store;
}

function savePensionStore(store: Record<string, PrivatePensionSimulation[]>): void {
  demoStorage.setItem(PENSIONS_KEY, JSON.stringify(store));
}

function financingStore(): Record<string, FinancingSimulation[]> {
  const raw = read<Record<string, unknown>>(demoStorage, FINANCING_KEY, { [DEMO_CLIENT_ID]: demoFinancingSimulations });
  const store: Record<string, FinancingSimulation[]> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!Array.isArray(value)) throw new Error(`Armazenamento ${FINANCING_KEY}:${key} inválido`);
    store[key] = value.map((item, index) => {
      try {
        const parsed = financingSimulationSchema.parse(item);
        assertDemoFinancingDerived(parsed);
        return parsed;
      } catch { throw new Error(`Registro ${FINANCING_KEY}:${key}[${index}] inválido`); }
    });
  }
  if (!demoStorage.getItem(FINANCING_KEY)) demoStorage.setItem(FINANCING_KEY, JSON.stringify(store));
  return store;
}

function saveFinancingStore(store: Record<string, FinancingSimulation[]>): void {
  demoStorage.setItem(FINANCING_KEY, JSON.stringify(store));
}

function comparisonStore(): Record<string, FinancingComparison[]> {
  const store = readStrictMap(demoStorage, COMPARISONS_KEY, {}, (value) => {
    if (!Array.isArray(value)) throw new Error('comparações inválidas');
    return value.map((item) => financingComparisonSchema.parse(item));
  });
  const simulations = Object.values(financingStore()).flat();
  for (const comparisons of Object.values(store)) for (const comparison of comparisons) assertDemoComparisonDerived(comparison, simulations);
  return store;
}

function saveComparisonStore(store: Record<string, FinancingComparison[]>): void {
  demoStorage.setItem(COMPARISONS_KEY, JSON.stringify(store));
}

function familyStore(): Record<string, FamilyMember[]> {
  const store = readStrictMap(demoStorage, FAMILY_KEY, { [DEMO_CLIENT_ID]: demoFamilyMembers }, (value) => {
    if (!Array.isArray(value)) throw new Error('familiares inválidos');
    return value.map((item) => familyMemberSchema.parse(item));
  });
  for (const [clientId, members] of Object.entries(store)) {
    if (members.some((member) => member.clientId !== clientId)) throw new Error('Familiar demo fora do namespace do cliente');
    const ids = new Set<string>();
    for (const member of members) {
      if (ids.has(member.id)) throw new Error('Armazenamento de familiares contém ID duplicado');
      ids.add(member.id);
    }
  }
  return store;
}

function saveFamilyStore(store: Record<string, FamilyMember[]>): void {
  demoStorage.setItem(FAMILY_KEY, JSON.stringify(store));
}

function exclusionStore(): Record<string, PlanningExclusionSelection[]> {
  const store = readStrictMap(demoStorage, EXCLUSIONS_KEY, {}, (value) => {
    if (!Array.isArray(value)) throw new Error('desconsiderações inválidas');
    return value.map((item) => planningExclusionSchema.parse(item));
  });
  for (const [clientId, selections] of Object.entries(store)) {
    const keys = new Set<string>();
    for (const selection of selections) {
      if (selection.clientId !== clientId) throw new Error('Desconsideração demo fora do namespace do cliente');
      const key = `${selection.scope}:${selection.scenarioId ?? ''}:${selection.targetType}`;
      if (keys.has(key)) throw new Error('Armazenamento de desconsiderações contém chave duplicada');
      keys.add(key);
    }
  }
  const financialsByClient = new Map<string, Set<string>>();
  for (const entry of financialStore()) {
    if (entry.kind !== 'expense') continue;
    const ids = financialsByClient.get(entry.clientId) ?? new Set<string>();
    ids.add(entry.id);
    financialsByClient.set(entry.clientId, ids);
  }
  const familyIdsByClient = new Map(Object.entries(familyStore()).map(([clientId, members]) => [clientId, new Set(members.map((member) => member.id))]));
  const guaranteeIdsByClient = new Map<string, Set<string>>();
  for (const exposure of guaranteeExposureStore()) {
    const ids = guaranteeIdsByClient.get(exposure.clientId) ?? new Set<string>();
    if (ids.has(exposure.id)) throw new Error('Armazenamento de exposições de garantia contém ID duplicado');
    ids.add(exposure.id);
    guaranteeIdsByClient.set(exposure.clientId, ids);
  }
  for (const [clientId, selections] of Object.entries(store)) {
    for (const selection of selections) {
      const allowedIds = selection.targetType === 'expense' ? (financialsByClient.get(clientId) ?? new Set<string>())
        : selection.targetType === 'family_member' ? (familyIdsByClient.get(clientId) ?? new Set<string>())
          : (guaranteeIdsByClient.get(clientId) ?? new Set<string>());
      if (selection.targetIds.some((id) => !allowedIds.has(id))) throw new Error('Desconsideração demo referencia alvo de outro cliente ou inexistente');
    }
  }
  return store;
}

function saveExclusionStore(store: Record<string, PlanningExclusionSelection[]>): void {
  demoStorage.setItem(EXCLUSIONS_KEY, JSON.stringify(store));
}

function fileStore(): Record<string, ClientFileLink[]> {
  const store = readStrictMap(demoStorage, FILES_KEY, { [DEMO_CLIENT_ID]: demoClientFiles }, (value) => {
    if (!Array.isArray(value)) throw new Error('arquivos inválidos');
    return value.map((item) => clientFileLinkSchema.parse(item));
  });
  for (const [clientId, files] of Object.entries(store)) {
    if (files.some((file) => file.clientId !== clientId)) throw new Error('Arquivo demo fora do namespace do cliente');
  }
  return store;
}

function saveFileStore(store: Record<string, ClientFileLink[]>): void {
  demoStorage.setItem(FILES_KEY, JSON.stringify(store));
}

function pipelineStore(): Record<string, ClientPipeline> {
  return readStrictMap(demoStorage, PIPELINE_KEY, { [DEMO_CLIENT_ID]: { clientId: DEMO_CLIENT_ID, stage: 'review', version: 1, history: [{ stage: 'review', at: '2026-09-28T14:22:00.000Z', actor: 'Paula Andrade' }] } }, (item) => clientPipelineSchema.parse(item));
}

function savePipelineStore(store: Record<string, ClientPipeline>): void {
  demoStorage.setItem(PIPELINE_KEY, JSON.stringify(store));
}

function clientAge(client: Client | undefined): number {
  if (!client?.birthDate) return client?.statedAge ?? 35;
  const birth = new Date(`${client.birthDate}T00:00:00.000Z`);
  const today = new Date();
  let age = today.getUTCFullYear() - birth.getUTCFullYear();
  if (today.getUTCMonth() < birth.getUTCMonth() || (today.getUTCMonth() === birth.getUTCMonth() && today.getUTCDate() < birth.getUTCDate())) age -= 1;
  return age;
}

function ensureScenarios(clientId: string, requestedScenarioId?: string): PlanScenario[] {
  const store = scenarioStore();
  let scenarios = store[clientId] ?? [];
  if (scenarios.length === 0) {
    scenarios = [{
      id: requestedScenarioId ?? (clientId === DEMO_CLIENT_ID ? DEMO_SCENARIO_ID : crypto.randomUUID()),
      planId: clientId === DEMO_CLIENT_ID ? DEMO_PLAN_ID : crypto.randomUUID(),
      name: 'Cenário base',
      isPrimary: true,
      visibleInComparison: true,
      version: 1,
      assumptionsVersion: 1,
      assumptions: clientId === DEMO_CLIENT_ID ? { ...demoAssumptions } : (() => {
        const currentAge = Math.max(0, Math.min(clientAge(clients().find((client) => client.id === clientId)), 119));
        const retirementAge = Math.min(120, Math.max(60, currentAge + 1));
        const withoutPostRates = { ...demoAssumptions };
        delete withoutPostRates.postRetirementNominalAnnualRatePct;
        delete withoutPostRates.postRetirementInflationAnnualRatePct;
        return { ...withoutPostRates, baseYear: new Date().getUTCFullYear(), baseMonth: new Date().getUTCMonth() + 1, currentAge, retirementAge, lifeExpectancyAge: Math.min(130, Math.max(90, retirementAge + 1)), currentReserve: 0, monthlyContribution: 0, desiredMonthlyIncome: 0, nominalAnnualRatePct: 8, inflationAnnualRatePct: 4, differentiatePostRetirementRates: false };
      })(),
    }];
    store[clientId] = scenarios;
    saveScenarioStore(store);
  } else if (requestedScenarioId && !scenarios.some((scenario) => scenario.id === requestedScenarioId)) {
    scenarios = [{ ...scenarios[0]!, id: requestedScenarioId, planId: clientId === DEMO_CLIENT_ID ? DEMO_PLAN_ID : crypto.randomUUID() }, ...scenarios.slice(1)];
    store[clientId] = scenarios;
    saveScenarioStore(store);
  }
  return scenarios;
}

function scenariosForClient(clientId: string): PlanScenario[] {
  return clientId === DEMO_CLIENT_ID ? ensureScenarios(clientId) : (scenarioStore()[clientId] ?? []);
}

function assertDemoClientScenario(clientId: string, scenarioId: string | null | undefined): void {
  if (!clients().some((client) => client.id === clientId)) throw new Error('Cliente não encontrado');
  if (scenarioId !== null && scenarioId !== undefined && !scenariosForClient(clientId).some((scenario) => scenario.id === scenarioId)) {
    throw new Error('Cenário não pertence ao cliente');
  }
}

function assertDemoClient(clientId: string): void {
  if (!clients().some((client) => client.id === clientId)) throw new Error('Cliente não encontrado');
}

async function sha256(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalJson(value));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

async function deterministicDemoUuid(value: unknown): Promise<string> {
  const digest = await sha256(value);
  const hex = `${digest.slice(0, 12)}4${digest.slice(13, 16)}${['8', '9', 'a', 'b'][Number.parseInt(digest[16]!, 16) % 4]}${digest.slice(17, 32)}`;
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

function sumMoneyStrings(values: readonly string[]): string {
  const cents = values.reduce((total, value) => {
    const match = /^(-?)(\d+)\.(\d{2})$/.exec(value);
    if (!match) throw new Error('Valor monetário inválido no cronograma');
    const amount = BigInt(match[2]!) * 100n + BigInt(match[3]!);
    return total + (match[1] === '-' ? -amount : amount);
  }, 0n);
  const sign = cents < 0n ? '-' : '';
  const absolute = cents < 0n ? -cents : cents;
  return `${sign}${absolute / 100n}.${String(absolute % 100n).padStart(2, '0')}`;
}

const GUARANTEE_EXPOSURE_KINDS: readonly GuaranteeExposure['kind'][] = ['immobilized_property', 'monthly_income', 'financial_resources', 'life_insurance'];
function effectiveGuaranteeExposures(exposures: readonly GuaranteeExposure[], scenarioId: string | null, exclusions: readonly PlanningExclusionSelection[] = []): GuaranteeExposure[] {
  return resolveEffectiveGuaranteeExposures(exposures, scenarioId, exclusions);
}

const demoOperationLocks = new Map<string, Promise<void>>();
async function withDemoOperationLock<T>(key: string, operation: () => Promise<T>, options: { requireCrossContext?: boolean } = {}): Promise<T> {
  const lockName = '__namespace__';
  void key;
  const serialized = async (): Promise<T> => {
    const previous = demoOperationLocks.get(lockName);
    let release!: () => void;
    const current = new Promise<void>((resolve) => { release = resolve; });
    demoOperationLocks.set(lockName, current);
    if (previous) await previous;
    try { return await operation(); }
    finally {
      release();
      if (demoOperationLocks.get(lockName) === current) demoOperationLocks.delete(lockName);
    }
  };
  const secureContext = typeof window === 'undefined' || window.isSecureContext === true;
  if (secureContext && typeof navigator !== 'undefined' && 'locks' in navigator) {
    return navigator.locks.request(`fintracks.demo:${lockName}`, { mode: 'exclusive' }, serialized);
  }
  const nodeEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env?.NODE_ENV;
  if (options.requireCrossContext !== false && nodeEnv !== 'test') throw new Error('Operações demo indisponíveis sem Web Locks; não é seguro operar concorrentemente entre abas.');
  return serialized();
}

async function withDemoStorageTransaction<T>(keys: readonly string[], operation: () => Promise<T>): Promise<T> {
  const before = new Map(keys.map((key) => [key, demoStorage.getItem(key)]));
  try {
    return await operation();
  } catch (error) {
    for (const [key, value] of before) {
      try { if (value === null) demoStorage.removeItem(key); else demoStorage.setItem(key, value); } catch { /* preserve the original mutation error */ }
    }
    throw error;
  }
}

async function verifiedReports(): Promise<ReportSnapshot[]> {
  const reports = reportStore();
  for (const report of reports) {
    const { sha256: digest, ...base } = report;
    if (digest !== await sha256(base)) throw new Error('Snapshot de relatório demo adulterado');
  }
  return reports;
}

async function verifiedCalculations(): Promise<CalculationSnapshot[]> {
  const calculations = calculationStore();
  for (const calculation of calculations) {
    const expected = await sha256({ clientId: calculation.clientId, scenarioId: calculation.scenarioId, engineVersion: calculation.engineVersion, input: calculation.input, output: calculation.output });
    if (calculation.sha256 !== expected) throw new Error('Snapshot de cálculo demo adulterado');
  }
  return calculations;
}

const asyncRepository: FintracksRepository & {
  getSession(): DemoSession | null;
  getSettings(): DemoSettings;
  saveSettings(value: DemoSettings): void;
} = {
  mode: 'demo',

  getSession() {
    return readDemoSession();
  },

  async login(email) {
    const normalizedEmail = email.trim().toLowerCase();
    const digest = await sha256(`demo-session:${normalizedEmail}`);
    const value: DemoSession = {
      id: `${digest.slice(0, 8)}-${digest.slice(8, 12)}-4${digest.slice(13, 16)}-8${digest.slice(17, 20)}-${digest.slice(20, 32)}`,
      email: normalizedEmail,
      name: 'Paula Andrade',
      role: { id: 'demo', name: 'Planejador' },
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(value));
    return value;
  },

  async logout() {
    return withDemoOperationLock('__logout__', async () => {
      sessionStorage.removeItem(SESSION_KEY);
      demoFileBlobs.clear();
    }, { requireCrossContext: false });
  },

  async currentUser() {
    return this.getSession();
  },

  async listClients() {
    return clients().filter((client) => client.status !== 'archived');
  },

  async listArchivedClients() {
    return clients().filter((client) => client.status === 'archived');
  },

  async getClient(id) {
    return clients().find(client => client.id === id) ?? null;
  },

  async createClient(input: CreateClient) {
    const validated = createClientSchema.parse(input);
    return withDemoOperationLock('client:create', () => withDemoStorageTransaction([CLIENTS_KEY, SCENARIO_KEY, PIPELINE_KEY, AUDIT_KEY], async () => {
      if (validated.id) {
        const existing = clients().find(client => client.id === validated.id);
        if (existing) {
          const sameContent = existing.status === 'draft'
            && existing.fullName === validated.fullName
            && existing.shortName === validated.shortName
            && existing.birthDate === validated.birthDate
            && existing.statedAge === validated.statedAge
            && existing.profession === validated.profession
            && existing.email === validated.email
            && existing.phone === validated.phone;
          if (!sameContent) throw new Error('O identificador da ficha já existe com conteúdo diferente.');
          return existing;
        }
      }
      const item: Client = {
        status: 'draft',
        ...validated,
        id: validated.id ?? crypto.randomUUID(),
        completeness: clientCompleteness(validated),
        updatedAt: new Date().toISOString(),
        version: 1,
      };
      clientSchema.parse(item);
      demoStorage.setItem(CLIENTS_KEY, JSON.stringify([item, ...clients()]));
      ensureScenarios(item.id);
      const pipelines = pipelineStore();
      pipelines[item.id] = { clientId: item.id, stage: 'new', version: 1, history: [] };
      savePipelineStore(pipelines);
      appendAudit(item.id, 'client.created', item.id);
      return item;
    }));
  },

  async updateClient(id, patch, expectedVersion) {
    return withDemoOperationLock(`client:${id}`, async () => withDemoStorageTransaction([CLIENTS_KEY, AUDIT_KEY], async () => {
      const validated = updateClientSchema.parse(patch);
      const all = clients();
      const index = all.findIndex(client => client.id === id);
      if (index < 0) throw new Error('Cliente não encontrado');
      if (all[index]!.version !== expectedVersion) throw new Error('Conflito de versão');
      const current = all[index]!;
      const candidate = {
      ...current,
      ...(validated.status !== undefined ? { status: validated.status } : {}),
      ...(validated.fullName !== undefined ? { fullName: validated.fullName } : {}),
      ...(validated.shortName !== undefined ? { shortName: validated.shortName } : {}),
      ...(validated.birthDate !== undefined ? { birthDate: validated.birthDate } : {}),
      ...(validated.statedAge !== undefined ? { statedAge: validated.statedAge } : {}),
      ...(validated.profession !== undefined ? { profession: validated.profession } : {}),
      ...(validated.email !== undefined ? { email: validated.email } : {}),
      ...(validated.phone !== undefined ? { phone: validated.phone } : {}),
      };
      const item: Client = clientSchema.parse({ ...candidate, completeness: clientCompleteness(candidate), version: expectedVersion + 1, updatedAt: new Date().toISOString() });
      all[index] = item;
      demoStorage.setItem(CLIENTS_KEY, JSON.stringify(all));
      appendAudit(id, 'client.updated', id, Object.keys(validated));
      return item;
    }));
  },

  async listFamilyMembers(clientId) {
    assertDemoClient(clientId);
    return familyStore()[clientId] ?? [];
  },

  async createFamilyMember(input: CreateFamilyMemberInput) {
    const validated = createFamilyMemberSchema.parse(input);
    assertDemoClient(validated.clientId);
    return withDemoOperationLock(`family:${validated.clientId}`, () => withDemoStorageTransaction([FAMILY_KEY, AUDIT_KEY], async () => {
      const now = new Date().toISOString();
      const member: FamilyMember = {
        id: crypto.randomUUID(), clientId: validated.clientId, fullName: validated.fullName.trim(), relationship: validated.relationship.trim(),
        birthDate: validated.birthDate ?? null, financiallyDependent: validated.financiallyDependent,
        successionDependent: validated.successionDependent, financialDependencyEnd: validated.financialDependencyEnd ?? null,
        protectionNeeded: validated.protectionNeeded, version: 1, createdAt: now, updatedAt: now,
      };
      familyMemberSchema.parse(member);
      const store = familyStore();
      store[validated.clientId] = [member, ...(store[validated.clientId] ?? [])];
      saveFamilyStore(store);
      appendAudit(validated.clientId, 'family.created', member.id);
      return member;
    }));
  },

  async updateFamilyMember(clientId, memberId, patch: UpdateFamilyMemberInput, expectedVersion) {
    assertDemoClient(clientId);
    const validated = updateFamilyMemberSchema.parse(patch);
    return withDemoOperationLock(`family:${clientId}`, () => withDemoStorageTransaction([FAMILY_KEY, AUDIT_KEY], async () => {
      const store = familyStore();
      const members = store[clientId] ?? [];
      const index = members.findIndex((member) => member.id === memberId);
      if (index < 0) throw new Error('Familiar não encontrado');
      if (members[index]!.version !== expectedVersion) throw new Error('Conflito de versão');
      const saved: FamilyMember = {
        ...members[index]!,
        ...(validated.fullName !== undefined ? { fullName: validated.fullName.trim() } : {}),
        ...(validated.relationship !== undefined ? { relationship: validated.relationship.trim() } : {}),
        ...(validated.birthDate !== undefined ? { birthDate: validated.birthDate } : {}),
        ...(validated.financiallyDependent !== undefined ? { financiallyDependent: validated.financiallyDependent } : {}),
        ...(validated.successionDependent !== undefined ? { successionDependent: validated.successionDependent } : {}),
        ...(validated.financialDependencyEnd !== undefined ? { financialDependencyEnd: validated.financialDependencyEnd } : {}),
        ...(validated.protectionNeeded !== undefined ? { protectionNeeded: validated.protectionNeeded } : {}),
        version: expectedVersion + 1, updatedAt: new Date().toISOString(),
      };
      familyMemberSchema.parse(saved);
      members[index] = saved;
      store[clientId] = members;
      saveFamilyStore(store);
      appendAudit(clientId, 'family.updated', memberId, Object.keys(validated));
      return saved;
    }));
  },

  async listClientFiles(clientId) {
    assertDemoClient(clientId);
    return (fileStore()[clientId] ?? []).filter((file) => file.status === 'active');
  },

  async listArchivedClientFiles(clientId) {
    assertDemoClient(clientId);
    return (fileStore()[clientId] ?? []).filter((file) => file.status === 'archived');
  },

  async createClientFile(input: CreateClientFileLinkInput) {
    const validated = createClientFileLinkSchema.parse(input);
    assertDemoClient(validated.clientId);
    // Caminho legado de referência: não recebe bytes e não cria receipt de upload.
    // Uploads novos devem usar uploadClientFile, que prova MIME, tamanho e hash.
    return withDemoOperationLock(`files:${validated.clientId}`, async () => {
    return withDemoStorageTransaction([FILES_KEY, AUDIT_KEY], async () => {
      const file: ClientFileLink = {
      id: crypto.randomUUID(), clientId: validated.clientId, name: validated.name.trim(), category: validated.category.trim(),
      mimeType: validated.mimeType, sizeBytes: validated.sizeBytes, ...(validated.contentHash ? { contentHash: validated.contentHash } : {}), storageRef: validated.storageRef,
      status: 'active', uploadedAt: new Date().toISOString(), uploadedBy: validated.uploadedBy ?? this.getSession()?.name ?? null, version: 1,
      };
      clientFileLinkSchema.parse(file);
      const store = fileStore();
      store[validated.clientId] = [file, ...(store[validated.clientId] ?? [])];
      saveFileStore(store);
      appendAudit(validated.clientId, 'file.linked', file.id);
      return file;
    });
    });
  },

  async uploadClientFile(input: UploadClientFileInput) {
    assertDemoClient(input.clientId);
    return withDemoOperationLock(`files:${input.clientId}`, async () => withDemoStorageTransaction([FILE_BLOBS_KEY, FILES_KEY, AUDIT_KEY, OPERATION_RECEIPTS_KEY], async () => {
    const actualMime = input.file.type || 'application/octet-stream';
    if (input.mimeType !== actualMime || input.sizeBytes !== input.file.size) throw new Error('O MIME e o tamanho informados devem coincidir com o arquivo selecionado');
    try { assertClientFileUploadPolicy({ mimeType: actualMime, sizeBytes: input.file.size }); }
    catch (error) { throw new Error(error instanceof Error ? error.message : 'Arquivo não atende à política de upload'); }
    const bytes = await readDemoBlobBytes(input.file);
    const contentHash = await sha256Bytes(bytes);
    const requestedKey = input.idempotencyKey?.trim() ?? '';
    if (requestedKey && (requestedKey.length < 8 || requestedKey.length > 200 || !/^[\x21-\x7e]+$/.test(requestedKey))) {
      throw new Error('A chave de idempotência do upload é inválida');
    }
    const evidence = await fileUploadEvidenceForContentHash(input, actualMime, contentHash);
    const { key, requestHash } = evidence;
    const receipts = operationReceiptStore();
    const blobs = persistedDemoBlobs();
    const files = fileStore();
    const previousReceipt = receipts.find((receipt) => receipt.kind === 'file.upload' && receipt.key === key);
    if (previousReceipt) {
      if (previousReceipt.requestHash !== requestHash || previousReceipt.clientId !== input.clientId) throw new Error('Idempotency-Key já usada com payload diferente');
      const existing = (files[input.clientId] ?? []).find((item) => item.id === previousReceipt.entityId);
      if (!existing) throw new Error('Receipt de upload sem vínculo; reconcilie antes de repetir');
      const existingBlob = blobs[existing.id];
      if (!existingBlob) throw new Error('Receipt de upload sem conteúdo; reconcilie antes de repetir');
      const existingBytes = decodeDemoBlob(existingBlob.data);
      if (existingBlob.mimeType !== existing.mimeType || await sha256Bytes(existingBytes) !== contentHash) throw new Error('Conteúdo do upload diverge do receipt existente');
      return existing;
    }
    const id = await deterministicDemoUuid({ operation: 'file.upload', clientId: input.clientId, key });
    const validated = createClientFileLinkSchema.parse({ clientId: input.clientId, name: input.name, category: input.category, uploadedBy: input.uploadedBy, mimeType: actualMime, sizeBytes: input.file.size, contentHash, storageRef: `demo://files/${id}` });
    assertDemoFileBytes(validated, bytes);
    const previousMemoryBlob = demoFileBlobs.get(id);
    blobs[id] = { mimeType: validated.mimeType, data: encodeDemoBlob(bytes) };
    const file: ClientFileLink = {
      id, clientId: validated.clientId, name: validated.name.trim(), category: validated.category.trim(),
      mimeType: validated.mimeType, sizeBytes: validated.sizeBytes, ...(validated.contentHash ? { contentHash: validated.contentHash } : {}), storageRef: validated.storageRef, status: 'active', uploadedAt: new Date().toISOString(),
      uploadedBy: validated.uploadedBy ?? this.getSession()?.name ?? null, version: 1,
    };
    clientFileLinkSchema.parse(file);
    try {
      savePersistedDemoBlobs(blobs);
      demoFileBlobs.set(id, input.file);
      files[validated.clientId] = [file, ...(files[validated.clientId] ?? [])];
      saveFileStore(files);
      saveOperationReceiptStore([...receipts, { key, kind: 'file.upload', clientId: validated.clientId, entityId: file.id, requestHash }]);
      appendAudit(validated.clientId, 'file.linked', file.id);
    } catch (error) {
      if (previousMemoryBlob) demoFileBlobs.set(id, previousMemoryBlob);
      else demoFileBlobs.delete(id);
      throw error;
    }
    return file;
    }));
  },

  async downloadClientFile(clientId: string, fileId: string) {
    assertDemoClient(clientId);
    const file = (fileStore()[clientId] ?? []).find((item) => item.id === fileId && item.status === 'active');
    if (!file) throw new Error('Arquivo não encontrado');
    const inMemory = demoFileBlobs.get(fileId);
    if (inMemory) {
      await assertDemoFileIntegrity(file, await readDemoBlobBytes(inMemory));
      return inMemory;
    }
    const persisted = persistedDemoBlobs()[fileId];
    if (!persisted) {
      const seed = demoClientFiles.find((item) => item.id === fileId && item.clientId === DEMO_CLIENT_ID);
      if (seed) {
        const regenerated = syntheticDemoBlob(seed);
        await assertDemoFileIntegrity(file, await readDemoBlobBytes(regenerated));
        demoFileBlobs.set(fileId, regenerated);
        return regenerated;
      }
      throw new Error('Arquivo demo expirado; faça o upload novamente nesta sessão');
    }
    const bytes = decodeDemoBlob(persisted.data);
    if (persisted.mimeType !== file.mimeType) throw new Error('Conteúdo persistido do arquivo demo não confere com os metadados');
    await assertDemoFileIntegrity(file, bytes);
    const arrayBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    const blob = new Blob([arrayBuffer], { type: persisted.mimeType });
    demoFileBlobs.set(fileId, blob);
    return blob;
  },

  async archiveClientFile(clientId, fileId, expectedVersion) {
    assertDemoClient(clientId);
    return withDemoOperationLock(`files:${clientId}`, async () => {
    return withDemoStorageTransaction([FILES_KEY, AUDIT_KEY], async () => {
    const store = fileStore();
    const files = store[clientId] ?? [];
    const index = files.findIndex((file) => file.id === fileId);
    if (index < 0) throw new Error('Arquivo não encontrado');
    if (files[index]!.version !== expectedVersion) throw new Error('Conflito de versão');
    if (files[index]!.status === 'archived') return files[index]!;
    const archived: ClientFileLink = { ...files[index]!, status: 'archived', version: expectedVersion + 1 };
    clientFileLinkSchema.parse(archived);
    files[index] = archived;
    store[clientId] = files;
    saveFileStore(store);
    appendAudit(clientId, 'file.archived', fileId, ['status']);
    return archived;
    });
    });
  },

  async restoreClientFile(clientId, fileId, expectedVersion) {
    assertDemoClient(clientId);
    return withDemoOperationLock(`files:${clientId}`, async () => {
    return withDemoStorageTransaction([FILES_KEY, AUDIT_KEY], async () => {
    const store = fileStore();
    const files = store[clientId] ?? [];
    const index = files.findIndex((file) => file.id === fileId);
    if (index < 0) throw new Error('Arquivo não encontrado');
    if (files[index]!.version !== expectedVersion) throw new Error('Conflito de versão');
    if (files[index]!.status === 'active') return files[index]!;
    const restored: ClientFileLink = { ...files[index]!, status: 'active', version: expectedVersion + 1 };
    clientFileLinkSchema.parse(restored);
    files[index] = restored;
    store[clientId] = files;
    saveFileStore(store);
    appendAudit(clientId, 'file.restored', fileId, ['status']);
    return restored;
    });
    });
  },

	async reconcileOperations(clientId) {
	  assertDemoClient(clientId);
	  const actorId = readDemoSession()?.id ?? 'anonymous';
  const pending = loadPendingOperations(actorId, clientId);
  if (!pending.healthy) throw new Error('O journal local de operações está indisponível; não é seguro declarar reconciliação concluída');
  const pendingEvidence = loadPendingOperationEvidence(actorId, clientId);
  if (!pendingEvidence.healthy) throw new Error('A evidência do journal local de operações está indisponível; não é seguro declarar reconciliação concluída');
  return withDemoOperationLock(`reconcile:${clientId}`, async () => {
    const receipts = operationReceiptStore();
    const files = fileStore()[clientId] ?? [];
    const blobs = persistedDemoBlobs();
    const followUps = followUpStore();
    let repaired = 0;
    for (const domain of pending.domains) {
      let verified = false;
      const evidence = pendingEvidence.byDomain[domain];
      if (domain === 'file') {
        const relevant = evidence?.kind === 'file.upload'
          ? receipts.filter((receipt) => receipt.kind === evidence.kind && receipt.clientId === clientId && receipt.key === evidence.key && receipt.requestHash === evidence.requestHash)
          : [];
        const checks = await Promise.all(relevant.map(async (receipt) => {
          const file = files.find((item) => item.id === receipt.entityId && item.contentHash);
          const blob = file ? blobs[file.id] : undefined;
          if (!file || !blob || blob.mimeType !== file.mimeType) return false;
          try {
            if (await fileUploadRequestHash(file) !== evidence?.requestHash) return false;
            await assertDemoFileIntegrity(file, decodeDemoBlob(blob.data));
            return true;
          } catch {
            return false;
          }
        }));
        verified = relevant.length > 0 && checks.every(Boolean);
      } else if (domain === 'follow-up') {
        const relevant = evidence?.kind === 'followup.create'
          ? receipts.filter((receipt) => receipt.kind === evidence.kind && receipt.clientId === clientId && receipt.key === evidence.key && receipt.requestHash === evidence.requestHash)
          : [];
        const checks = await Promise.all(relevant.map(async (receipt) => {
          const item = followUps.find((candidate) => candidate.id === receipt.entityId && candidate.clientId === clientId);
          return item ? await followUpRequestHash(item) === evidence?.requestHash : false;
        }));
        verified = relevant.length > 0 && checks.every(Boolean);
      }
      if (verified) {
        const next = await clearPendingOperationDomain(actorId, clientId, domain);
        if (!next.healthy) throw new Error('Não foi possível persistir a reconciliação local com segurança');
        repaired += 1;
      }
    }
    const remaining = loadPendingOperations(actorId, clientId);
    if (!remaining.healthy) throw new Error('O journal local de operações ficou indisponível durante a reconciliação');
    return { clientId, repaired, pending: remaining.domains.length };
  });
},

	async getClientProvisioning(clientId) {
	  assertDemoClient(clientId);
	  throw new Error('Provisioning remoto não está disponível no adapter demo; use o gateway Edge');
	},

	async reconcileClientProvisioning(clientId) {
	  assertDemoClient(clientId);
	  throw new Error('Reconciliação de provisioning não está disponível no adapter demo; use o gateway Edge');
	},

	async listScenarios(clientId) {
    assertDemoClient(clientId);
    const scenarios = scenariosForClient(clientId);
    return scenarios.map((scenario) => ({ ...scenario, assumptions: { ...scenario.assumptions } }));
  },

  async getScenario(clientId, id) {
    assertDemoClient(clientId);
    const scenarios = scenariosForClient(clientId);
    return scenarios.find((scenario) => scenario.id === id) ?? null;
  },

  async createScenario(clientId, input: CreateScenarioInput) {
    assertDemoClientScenario(clientId, null);
    const validated = createScenarioSchema.parse(input);
    return withDemoOperationLock(`scenario:${clientId}`, async () => {
    return withDemoStorageTransaction([SCENARIO_KEY, AUDIT_KEY], async () => {
    const store = scenarioStore();
    const scenarios = ensureScenarios(clientId);
    const source = validated.sourceScenarioId
      ? scenarios.find((scenario) => scenario.id === validated.sourceScenarioId)
      : scenarios.find((scenario) => scenario.isPrimary) ?? scenarios[0];
    if (!source) throw new Error('Não há cenário-base para duplicar');
    const name = validated.name.trim();
    if (name.length < 2) throw new Error('O cenário precisa de um nome');
    const scenario: PlanScenario = {
      ...source,
      id: crypto.randomUUID(),
      planId: source.planId,
      name,
      isPrimary: validated.makePrimary === true,
      visibleInComparison: true,
      version: 1,
      assumptionsVersion: 1,
      assumptions: { ...source.assumptions },
    };
    const next = validated.makePrimary
      ? [...scenarios.map((item) => item.isPrimary ? { ...item, isPrimary: false, version: item.version + 1 } : item), scenario]
      : [...scenarios, scenario];
    planScenarioSchema.parse(scenario);
    store[clientId] = next.map((item) => planScenarioSchema.parse(item));
    saveScenarioStore(store);
    appendAudit(clientId, 'scenario.created', scenario.id, ['name', 'assumptions', 'isPrimary']);
    return scenario;
    });
    });
  },

  async saveScenario(clientId, scenario: PlanScenario) {
    assertDemoClient(clientId);
    planScenarioSchema.parse(scenario);
    const assumptions = scenarioAssumptionsSchema.parse(scenario.assumptions);
    return withDemoOperationLock(`scenario:${clientId}`, async () => {
    return withDemoStorageTransaction([SCENARIO_KEY, AUDIT_KEY], async () => {
    const store = scenarioStore();
    const scenarios = ensureScenarios(clientId);
    const index = scenarios.findIndex((item) => item.id === scenario.id);
    if (index < 0) throw new Error('Cenário não encontrado');
    if (scenarios[index]!.planId !== scenario.planId) throw new Error('Plano não pertence ao cliente');
    if (scenarios[index]!.version !== scenario.version) throw new Error('Conflito de versão');
    const wasPrimary = scenarios[index]!.isPrimary;
    const saved = {
      ...scenario,
      assumptions,
      isPrimary: scenario.isPrimary || wasPrimary,
      version: scenario.version + 1,
      assumptionsVersion: scenario.assumptionsVersion + 1,
    };
    planScenarioSchema.parse(saved);
    store[clientId] = scenarios.map((item, itemIndex) => {
      if (itemIndex === index) return saved;
      return saved.isPrimary && item.isPrimary ? { ...item, isPrimary: false, version: item.version + 1 } : item;
    });
    saveScenarioStore(store);
    appendAudit(clientId, 'scenario.updated', saved.id, ['name', 'assumptions', 'isPrimary', 'version']);
    return saved;
    });
    });
  },

  async listExclusionSelections(clientId) {
    assertDemoClient(clientId);
    return (exclusionStore()[clientId] ?? []).map((item) => ({ ...item, targetIds: [...item.targetIds], pendingLegacyIds: [...item.pendingLegacyIds] }));
  },

  async saveExclusionSelection(clientId, input: SavePlanningExclusionInput, expectedVersion) {
    assertDemoClient(clientId);
    const validated = savePlanningExclusionSchema.parse(input);
    if (!Number.isInteger(expectedVersion) || expectedVersion < 0) throw new Error('Versão esperada da desconsideração inválida');
    if (validated.scope === 'scenario' && !scenariosForClient(clientId).some((scenario) => scenario.id === validated.scenarioId)) throw new Error('O cenário da desconsideração não pertence ao cliente');
    if (validated.targetType === 'family_member') {
      const familyIds = new Set((familyStore()[clientId] ?? []).filter((member) => member.clientId === clientId).map((member) => member.id));
      if (validated.targetIds.some((id) => !familyIds.has(id))) throw new Error('A desconsideração referencia familiar de outro cliente ou inexistente');
    }
    if (validated.targetType === 'expense') {
      const expenseIds = new Set(financialStore().filter((entry) => entry.clientId === clientId && entry.kind === 'expense').map((entry) => entry.id));
      if (validated.targetIds.some((id) => !expenseIds.has(id))) throw new Error('A desconsideração referencia despesa de outro cliente ou inexistente');
    }
    if (validated.targetType === 'guarantee') {
      const guaranteeIds = new Set(guaranteeExposureStore().filter((exposure) => exposure.clientId === clientId).map((exposure) => exposure.id));
      if (validated.targetIds.some((id) => !guaranteeIds.has(id))) throw new Error('A desconsideração referencia exposição de garantia de outro cliente ou inexistente');
    }
    return withDemoOperationLock(`exclusion:${clientId}`, () => withDemoStorageTransaction([EXCLUSIONS_KEY, AUDIT_KEY], async () => {
      const store = exclusionStore();
      const selections = store[clientId] ?? [];
      const scenarioId = validated.scenarioId ?? null;
      const index = selections.findIndex((item) => item.scope === validated.scope && item.scenarioId === scenarioId && item.targetType === validated.targetType);
      const current = index >= 0 ? selections[index] : undefined;
      const currentVersion = current?.version ?? 0;
      if (currentVersion !== expectedVersion) throw new Error('A desconsideração foi alterada em outra sessão');
      const now = new Date().toISOString();
      const saved = planningExclusionSchema.parse({
        id: current?.id ?? crypto.randomUUID(), clientId, scenarioId, scope: validated.scope, targetType: validated.targetType,
        state: validated.state, targetIds: validated.targetIds, pendingLegacyIds: validated.pendingLegacyIds,
        version: currentVersion + 1, updatedAt: now,
      });
      store[clientId] = index >= 0 ? selections.map((item, itemIndex) => itemIndex === index ? saved : item) : [saved, ...selections];
      saveExclusionStore(store);
      appendAudit(clientId, 'planning.exclusion.updated', saved.id, ['planning']);
      return saved;
    }));
  },

  async listGoals(clientId) {
    assertDemoClient(clientId);
    const scenarioIds = new Set(scenariosForClient(clientId).map((scenario) => scenario.id));
    const goals = goalStore()[clientId] ?? [];
    if (goals.some((goal) => goal.scenarioId !== null && !scenarioIds.has(goal.scenarioId))) throw new Error('Meta referencia um cenário inexistente');
    return goals.filter((goal) => goal.status !== 'archived');
  },

  async listArchivedGoals(clientId) {
    assertDemoClient(clientId);
    const scenarioIds = new Set(scenariosForClient(clientId).map((scenario) => scenario.id));
    const goals = goalStore()[clientId] ?? [];
    if (goals.some((goal) => goal.scenarioId !== null && !scenarioIds.has(goal.scenarioId))) throw new Error('Meta referencia um cenário inexistente');
    return goals.filter((goal) => goal.status === 'archived');
  },

  async createGoal(clientId, input: CreateGoalInput) {
    const validated = createGoalSchema.parse(input);
    assertDemoClientScenario(clientId, validated.scenarioId);
    return withDemoOperationLock(`goals:${clientId}`, () => withDemoStorageTransaction([GOALS_KEY, AUDIT_KEY], async () => {
    const store = goalStore();
    const goalId = await deterministicDemoUuid({ operation: 'goal.create', clientId, input: validated });
    const existing = (store[clientId] ?? []).find((item) => item.id === goalId);
    if (existing) return existing;
    const now = new Date().toISOString();
    const goal: ClientGoal = {
      id: goalId, clientId, scenarioId: validated.scenarioId ?? null,
      title: validated.title.trim(), category: validated.category.trim(), priority: validated.priority,
      targetDate: validated.targetDate, targetValue: moneyFromNumber(validated.targetValue), reservedValue: moneyFromNumber(validated.reservedValue),
      periodicity: validated.periodicity, status: 'active', version: 1, createdAt: now, updatedAt: now,
    };
    store[clientId] = [goal, ...(store[clientId] ?? [])];
    saveGoalStore(store);
    appendAudit(clientId, 'goal.created', goal.id, ['goal']);
    return goal;
    }));
  },

  async updateGoal(clientId, goalId, patch, expectedVersion) {
    assertDemoClient(clientId);
    const validated = createGoalSchema.partial().extend({ status: goalStatusSchema.optional() }).parse(patch);
    return withDemoOperationLock(`goals:${clientId}`, () => withDemoStorageTransaction([GOALS_KEY, AUDIT_KEY], async () => {
    const store = goalStore();
    const goals = store[clientId] ?? [];
    const index = goals.findIndex((goal) => goal.id === goalId);
    if (index < 0) throw new Error('Meta não encontrada');
    if (goals[index]!.version !== expectedVersion) throw new Error('Conflito de versão');
    const current = goals[index]!;
    if (validated.scenarioId !== undefined) assertDemoClientScenario(clientId, validated.scenarioId);
    const saved: ClientGoal = {
      ...current,
      ...(validated.scenarioId !== undefined ? { scenarioId: validated.scenarioId } : {}),
      ...(validated.title !== undefined ? { title: validated.title.trim() } : {}),
      ...(validated.category !== undefined ? { category: validated.category.trim() } : {}),
      ...(validated.priority !== undefined ? { priority: validated.priority } : {}),
      ...(validated.targetDate !== undefined ? { targetDate: validated.targetDate } : {}),
      ...(validated.targetValue !== undefined ? { targetValue: moneyFromNumber(validated.targetValue) } : {}),
      ...(validated.reservedValue !== undefined ? { reservedValue: moneyFromNumber(validated.reservedValue) } : {}),
      ...(validated.periodicity !== undefined ? { periodicity: validated.periodicity } : {}),
      ...(validated.status !== undefined ? { status: validated.status } : {}),
      version: expectedVersion + 1, updatedAt: new Date().toISOString(),
    };
    store[clientId] = goals.map((goal, goalIndex) => goalIndex === index ? saved : goal);
    saveGoalStore(store);
    appendAudit(clientId, validated.status === 'archived' ? 'goal.archived' : 'goal.updated', saved.id, ['goal']);
    return saved;
    }));
  },

  async listFinancialEntries(clientId) {
    assertDemoClient(clientId);
    return financialStore().filter((entry) => entry.clientId === clientId && entry.active);
  },

  async listArchivedFinancialEntries(clientId) {
    assertDemoClient(clientId);
    return financialStore().filter((entry) => entry.clientId === clientId && !entry.active);
  },

  async listGuaranteeExposures(clientId, scenarioId) {
    assertDemoClient(clientId);
    const exposures = guaranteeExposureStore().filter((exposure) => exposure.clientId === clientId);
    if (scenarioId === undefined) return exposures;
    return exposures.filter((exposure) => exposure.scenarioId === null || exposure.scenarioId === scenarioId);
  },

  async createFinancialEntry(input: CreateFinancialEntryInput) {
    const validated = createFinancialEntrySchema.parse(input);
    assertDemoClient(validated.clientId);
    return withDemoOperationLock(`financials:${validated.clientId}`, () => withDemoStorageTransaction([FINANCIALS_KEY, AUDIT_KEY], async () => {
    const now = new Date().toISOString();
    const entry: ClientFinancialEntry = {
      id: crypto.randomUUID(), clientId: validated.clientId, kind: validated.kind, category: validated.category.trim(),
      title: validated.title.trim(), institution: validated.institution?.trim() || null,
      currentValue: moneyFromNumber(validated.currentValue), monthlyValue: moneyFromNumber(validated.monthlyValue),
      retirementMonthlyValue: moneyFromNumber(validated.retirementMonthlyValue), liquidity: validated.liquidity,
      periodicity: validated.periodicity, active: true, version: 1, createdAt: now, updatedAt: now,
    };
    saveFinancialStore([entry, ...financialStore()]);
    appendAudit(validated.clientId, 'financial.created', entry.id, ['financial']);
    return entry;
    }));
  },

  async updateFinancialEntry(clientId, entryId, patch: UpdateFinancialEntryInput, expectedVersion) {
    assertDemoClient(clientId);
    const validated = createFinancialEntrySchema.partial().extend({ active: z.boolean().optional() }).parse({ ...patch, clientId });
    return withDemoOperationLock(`financials:${clientId}`, () => withDemoStorageTransaction([FINANCIALS_KEY, AUDIT_KEY], async () => {
    const all = financialStore();
    const index = all.findIndex((entry) => entry.clientId === clientId && entry.id === entryId);
    if (index < 0) throw new Error('Registro financeiro não encontrado');
    if (all[index]!.version !== expectedVersion) throw new Error('Conflito de versão');
    const current = all[index]!;
    const saved: ClientFinancialEntry = {
      ...current,
      ...(validated.kind !== undefined ? { kind: validated.kind } : {}),
      ...(validated.category !== undefined ? { category: validated.category.trim() } : {}),
      ...(validated.title !== undefined ? { title: validated.title.trim() } : {}),
      ...(validated.institution !== undefined ? { institution: validated.institution?.trim() || null } : {}),
      ...(validated.currentValue !== undefined ? { currentValue: moneyFromNumber(validated.currentValue) } : {}),
      ...(validated.monthlyValue !== undefined ? { monthlyValue: moneyFromNumber(validated.monthlyValue) } : {}),
      ...(validated.retirementMonthlyValue !== undefined ? { retirementMonthlyValue: moneyFromNumber(validated.retirementMonthlyValue) } : {}),
      ...(validated.liquidity !== undefined ? { liquidity: validated.liquidity } : {}),
      ...(validated.periodicity !== undefined ? { periodicity: validated.periodicity } : {}),
      ...(patch.active !== undefined ? { active: patch.active } : {}),
      version: expectedVersion + 1, updatedAt: new Date().toISOString(),
    };
    all[index] = saved;
    saveFinancialStore(all);
    appendAudit(clientId, 'financial.updated', saved.id, ['financial']);
    return saved;
    }));
  },

  async getProtectionNeed(clientId) {
    assertDemoClient(clientId);
    return protectionStore()[clientId] ?? null;
  },

  async saveProtectionNeed(input: CreateProtectionNeedInput, expectedVersion: number) {
    const validated = createProtectionNeedSchema.parse(input);
    assertDemoClient(validated.clientId);
    if (!Number.isInteger(expectedVersion) || expectedVersion < 0) throw new Error('Versão esperada de proteção inválida');
    return withDemoOperationLock(`protection:${validated.clientId}`, () => withDemoStorageTransaction([PROTECTION_KEY, AUDIT_KEY], async () => {
      const current = protectionStore()[validated.clientId] ?? null;
      const currentVersion = current?.version ?? 0;
      if (currentVersion !== expectedVersion) throw new Error('Conflito de versão');
      const selections = await this.listExclusionSelections(validated.clientId);
      const scenarioIds = new Set((await this.listScenarios(validated.clientId)).map((scenario) => scenario.id));
      if (validated.guaranteesScenarioId !== null && !scenarioIds.has(validated.guaranteesScenarioId)) throw new Error('O cenário das garantias não pertence ao cliente');
      const excludedFamilyIds = new Set(resolveExcludedTargetIds(selections, null, 'family_member'));
      const familyMembers = (familyStore()[validated.clientId] ?? []).filter((member) => !excludedFamilyIds.has(member.id));
      const family = familyMembers.map((member) => ({ id: member.id, version: member.version })).sort((left, right) => left.id.localeCompare(right.id));
      const guaranteeExposures = guaranteeExposureStore().filter((exposure) => exposure.clientId === validated.clientId);
      const availableGuarantees = effectiveGuaranteeExposures(guaranteeExposures, validated.guaranteesScenarioId);
      const effectiveGuarantees = effectiveGuaranteeExposures(guaranteeExposures, validated.guaranteesScenarioId, selections);
      if (validated.guaranteesMode === 'canonical') {
        const kinds = new Set(availableGuarantees.map((exposure) => exposure.kind));
        if (availableGuarantees.length !== GUARANTEE_EXPOSURE_KINDS.length || kinds.size !== GUARANTEE_EXPOSURE_KINDS.length || availableGuarantees.some((exposure) => exposure.bindingState !== 'verified' || !exposure.bindingReceipt || !exposure.bindingReceiptSha256 || !exposure.bindingExposureSha256)) throw new Error('As quatro exposições canônicas de garantia não estão verificadas para o cenário.');
        if ((await Promise.all(availableGuarantees.map(guaranteeExposureBindingMatches))).some((matches) => !matches)) throw new Error('As exposições canônicas de garantia não possuem receipt criptograficamente vinculado.');
        if (sumMoneyStrings(effectiveGuarantees.map((exposure) => exposure.value)) !== moneyFromNumber(validated.currentGuarantees)) throw new Error('O valor de garantias não corresponde às exposições canônicas verificadas.');
      }
      if (validated.dependents !== protectionDependentsFromFamily(familyMembers)) throw new Error('A quantidade de dependentes deve corresponder à Família carregada');
      const sourceRevisionFingerprint = await sha256(protectionRevisionPayload({ clientId: validated.clientId, family, guaranteesMode: validated.guaranteesMode, guaranteesScenarioId: validated.guaranteesScenarioId, guaranteeExposures: effectiveGuarantees, exclusions: selections }));
      const saved = { ...calculateProtectionPlan(validated, current), sourceRevisionFingerprint };
      const store = protectionStore();
      store[validated.clientId] = saved;
      saveProtectionStore(store);
      appendAudit(validated.clientId, 'protection.updated', saved.id, ['protection']);
      return saved;
    }));
  },

  async listPrivatePensionSimulations(clientId) {
    assertDemoClient(clientId);
    return pensionStore()[clientId] ?? [];
  },

  async createPrivatePensionSimulation(input: CreatePrivatePensionSimulationInput) {
    const validated = createPrivatePensionSimulationSchema.parse(input);
    assertDemoClient(validated.clientId);
    return withDemoOperationLock(`pension:${validated.clientId}`, () => withDemoStorageTransaction([PENSIONS_KEY, AUDIT_KEY], async () => {
    const projection = privatePensionProjection(validated);
    const now = new Date().toISOString();
    const simulation: PrivatePensionSimulation = {
      id: crypto.randomUUID(), clientId: validated.clientId, name: validated.name.trim(), product: validated.product,
      taxRegime: validated.taxRegime, socialSecurity: validated.socialSecurity,
      annualTaxableIncome: moneyFromNumber(validated.annualTaxableIncome), annualDeductibleExpenses: moneyFromNumber(validated.annualDeductibleExpenses),
      currentReserve: moneyFromNumber(validated.currentReserve), monthlyContribution: moneyFromNumber(validated.monthlyContribution),
      annualReturnPct: validated.annualReturnPct, inflationAnnualPct: validated.inflationAnnualPct,
      accumulationYears: validated.accumulationYears, retirementIncomeYears: validated.retirementIncomeYears,
      result: {
        suggestedPgblAnnual: projection.suggestedPgblAnnual.toFixed(2), taxBenefit: projection.taxBenefit.toFixed(2),
        grossReserve: projection.grossReserve.toFixed(2), taxableRescue: projection.taxableRescue.toFixed(2),
        estimatedTax: projection.estimatedTax.toFixed(2), netReserve: projection.netReserve.toFixed(2),
        sustainableMonthlyIncome: projection.sustainableMonthlyIncome.toFixed(2), taxRatePct: projection.taxRatePct,
      }, version: 1, createdAt: now, updatedAt: now,
    };
    const store = pensionStore();
    store[validated.clientId] = [simulation, ...(store[validated.clientId] ?? [])];
    savePensionStore(store);
    appendAudit(validated.clientId, 'pension.created', simulation.id, ['pension']);
    return simulation;
    }));
  },

  async listFinancingSimulations(clientId) {
    assertDemoClient(clientId);
    return financingStore()[clientId] ?? [];
  },

  async createFinancingSimulation(input: CreateFinancingSimulationInput) {
    const validated = createFinancingSimulationSchema.parse(input);
    assertDemoClient(validated.clientId);
    return withDemoOperationLock(`financing:${validated.clientId}`, () => withDemoStorageTransaction([FINANCING_KEY, AUDIT_KEY], async () => {
    const rows = validated.kind === 'sac'
      ? sacSchedule(validated.principal, validated.termMonths, validated.annualInterestPct, validated.annualIndexationPct).map((row) => ({
        installment: row.number, payment: row.payment.toFixed(2), principal: row.amortization.toFixed(2), interest: row.interest.toFixed(2), balance: row.closingBalance.toFixed(2),
      }))
      : consortiumScheduleRowsFromInput({
        credit: validated.principal, termMonths: validated.termMonths, anticipationFeePct: validated.anticipationFeePct,
        administrationFeePct: validated.administrationFeePct, reserveFundPct: validated.reserveFundPct, monthlyInsurancePct: validated.monthlyInsurancePct,
        installmentReducerPct: validated.installmentReducerPct, contemplationInstallment: validated.contemplationInstallment,
        totalBidPct: validated.totalBidPct, embeddedBidPct: validated.embeddedBidPct, annualAdjustmentPct: validated.annualAdjustmentPct,
      }).map((row, index) => ({ installment: index + 1, payment: row.payment.toFixed(2), principal: row.principal.toFixed(2), interest: row.interest.toFixed(2), balance: row.balance.toFixed(2) }));
    const now = new Date().toISOString();
    const totalPaid = sumMoneyStrings(rows.map((row) => row.payment));
    const simulation: FinancingSimulation = {
      id: crypto.randomUUID(), clientId: validated.clientId, name: validated.name.trim(), kind: validated.kind,
      principal: moneyFromNumber(validated.principal), termMonths: validated.termMonths, annualInterestPct: validated.annualInterestPct,
      annualIndexationPct: validated.annualIndexationPct, administrationFeePct: validated.administrationFeePct,
      reserveFundPct: validated.reserveFundPct, anticipationFeePct: validated.anticipationFeePct, monthlyInsurancePct: validated.monthlyInsurancePct,
      installmentReducerPct: validated.installmentReducerPct, contemplationInstallment: validated.contemplationInstallment,
      totalBidPct: validated.totalBidPct, embeddedBidPct: validated.embeddedBidPct, annualAdjustmentPct: validated.annualAdjustmentPct,
      firstPayment: (rows[0]?.payment ?? '0.00'), totalPaid, lastPayment: (rows.at(-1)?.payment ?? '0.00'),
      schedule: rows, version: 1, createdAt: now, updatedAt: now,
    };
    const store = financingStore();
    store[validated.clientId] = [simulation, ...(store[validated.clientId] ?? [])];
    saveFinancingStore(store);
    appendAudit(validated.clientId, 'financing.created', simulation.id, ['financing']);
    return simulation;
    }));
  },

  async listFinancingComparisons(clientId) {
    assertDemoClient(clientId);
    return comparisonStore()[clientId] ?? [];
  },

  async createFinancingComparison(input: CreateFinancingComparisonInput) {
    const validated = createFinancingComparisonSchema.parse(input);
    assertDemoClient(validated.clientId);
    return withDemoOperationLock(`financing:${validated.clientId}`, () => withDemoStorageTransaction([FINANCING_KEY, COMPARISONS_KEY, AUDIT_KEY], async () => {
    const simulations = financingStore()[validated.clientId] ?? [];
    const suggested = simulations.find((item) => item.id === validated.suggestedSimulationId);
    const alternative = simulations.find((item) => item.id === validated.alternativeSimulationId);
    if (!suggested || !alternative) throw new Error('Simulações financeiras não encontradas');
    const suggestedPayments = suggested.schedule.map((row) => persistedMoneyNumber(row.payment));
    const alternativePayments = alternative.schedule.map((row) => persistedMoneyNumber(row.payment));
    const rows = compareFinancingSchedules(suggestedPayments, alternativePayments, validated.annualOpportunityRatePct);
    const annualRows = annualizeFinancingComparison(rows);
    const cashflows = [0, ...rows.map((row) => row.difference.toNumber())];
    const tirAnnualPct = periodicInternalRateOfReturnPct(cashflows);
    const now = new Date().toISOString();
    const comparison: FinancingComparison = {
      id: crypto.randomUUID(), clientId: validated.clientId, name: validated.name.trim(), suggestedSimulationId: suggested.id, alternativeSimulationId: alternative.id,
      annualOpportunityRatePct: validated.annualOpportunityRatePct,
      suggestedPresentValue: presentValueSchedule(suggestedPayments, validated.annualOpportunityRatePct).toFixed(2),
      alternativePresentValue: presentValueSchedule(alternativePayments, validated.annualOpportunityRatePct).toFixed(2),
      tirAnnualPct,
      rows: rows.map((row) => ({ installment: row.installment, suggestedPayment: row.suggestedPayment.toFixed(2), alternativePayment: row.alternativePayment.toFixed(2), difference: row.difference.toFixed(2), accumulatedDifference: row.accumulatedDifference.toFixed(2) })),
      annualRows: annualRows.map((row) => ({ year: row.year, suggestedPayment: row.suggestedPayment.toFixed(2), alternativePayment: row.alternativePayment.toFixed(2), difference: row.difference.toFixed(2), accumulatedDifference: row.accumulatedDifference.toFixed(2) })),
      version: 1, createdAt: now, updatedAt: now,
    };
    financingComparisonSchema.parse(comparison);
    const store = comparisonStore();
    store[validated.clientId] = [comparison, ...(store[validated.clientId] ?? [])];
    saveComparisonStore(store);
    appendAudit(validated.clientId, 'comparison.created', comparison.id, ['comparison']);
    return comparison;
    }));
  },

  async getClientPipeline(clientId) {
    assertDemoClient(clientId);
    return withDemoOperationLock(`pipeline:${clientId}`, () => withDemoStorageTransaction([PIPELINE_KEY, AUDIT_KEY], async () => {
    const store = pipelineStore();
    const existing = store[clientId];
    if (existing) return existing;
    const created: ClientPipeline = { clientId, stage: 'new', version: 1, history: [] };
    store[clientId] = created;
    savePipelineStore(store);
    appendAudit(clientId, 'pipeline.updated', clientId, ['pipeline']);
    return created;
    }));
  },

  async updateClientPipeline(clientId, stage: ClientPipelineStage, expectedVersion: number) {
    return withDemoOperationLock(`pipeline:${clientId}`, () => withDemoStorageTransaction([PIPELINE_KEY, AUDIT_KEY], async () => {
      assertDemoClient(clientId);
      const store = pipelineStore();
      const current = store[clientId] ?? { clientId, stage: 'new' as const, version: 1, history: [] };
      if (current.version !== expectedVersion) throw new Error('Conflito de versão do funil');
      if (current.stage === stage) return current;
      const saved: ClientPipeline = { clientId, stage, version: current.version + 1, history: [...current.history, { stage, at: new Date().toISOString(), actor: this.getSession()?.name ?? null }] };
      store[clientId] = saved;
      savePipelineStore(store);
      appendAudit(clientId, 'pipeline.updated', clientId, ['pipeline']);
      return saved;
    }));
  },

  async listFollowUp(clientId) {
    if (clientId) assertDemoClient(clientId);
    return followUpStore()
      .filter((item) => !clientId || item.clientId === clientId)
      .sort((left, right) => (left.dueAt ?? '9999').localeCompare(right.dueAt ?? '9999'));
  },

  async createFollowUp(input: CreateFollowUpInput) {
    const validated = createFollowUpSchema.parse(input);
    assertDemoClient(validated.clientId);
    return withDemoOperationLock(`followup:${validated.clientId}`, () => withDemoStorageTransaction([FOLLOW_UP_KEY, AUDIT_KEY, OPERATION_RECEIPTS_KEY], async () => {
    const evidence = await followUpEvidenceForValidated(validated);
    const { key, requestHash } = evidence;
    const receipts = operationReceiptStore();
    const previous = receipts.find((receipt) => receipt.key === key && receipt.kind === 'followup.create');
    if (previous) {
      if (previous.requestHash !== requestHash || previous.clientId !== validated.clientId) throw new Error('Idempotency-Key já usada com payload diferente');
      const existing = followUpStore().find((item) => item.id === previous.entityId);
      if (!existing) throw new Error('Receipt de acompanhamento sem entidade; reconcilie antes de repetir');
      return existing;
    }
    const now = new Date().toISOString();
    const item: ClientFollowUp = {
      id: await deterministicDemoUuid({ operation: 'followup.create', key, clientId: validated.clientId }), clientId: validated.clientId, type: validated.type, title: validated.title.trim(),
      description: validated.description.trim(), status: 'open', dueAt: validated.dueAt ?? null,
      assignedTo: validated.assignedTo ?? null, version: 1, createdAt: now, updatedAt: now,
    };
    clientFollowUpSchema.parse(item);
    saveFollowUpStore([item, ...followUpStore()]);
    saveOperationReceiptStore([...receipts, { key, kind: 'followup.create', clientId: validated.clientId, entityId: item.id, requestHash }]);
    appendAudit(validated.clientId, 'followup.created', item.id, ['follow_up']);
    return item;
    }));
  },

  async updateFollowUp(clientId, itemId, patch: UpdateFollowUpInput, expectedVersion) {
    assertDemoClient(clientId);
    const validated = updateFollowUpSchema.parse(patch);
    return withDemoOperationLock(`followup:${clientId}`, () => withDemoStorageTransaction([FOLLOW_UP_KEY, AUDIT_KEY], async () => {
    const all = followUpStore();
    const index = all.findIndex((item) => item.clientId === clientId && item.id === itemId);
    if (index < 0) throw new Error('Ação de acompanhamento não encontrada');
    if (all[index]!.version !== expectedVersion) throw new Error('Conflito de versão');
    const current = all[index]!;
    const saved: ClientFollowUp = {
      ...current,
      ...(validated.type !== undefined ? { type: validated.type } : {}),
      ...(validated.title !== undefined ? { title: validated.title.trim() } : {}),
      ...(validated.description !== undefined ? { description: validated.description.trim() } : {}),
      ...(validated.dueAt !== undefined ? { dueAt: validated.dueAt } : {}),
      ...(validated.assignedTo !== undefined ? { assignedTo: validated.assignedTo } : {}),
      ...(validated.status !== undefined ? { status: validated.status } : {}),
      version: expectedVersion + 1, updatedAt: new Date().toISOString(),
    };
    clientFollowUpSchema.parse(saved);
    all[index] = saved;
    saveFollowUpStore(all);
    appendAudit(clientId, saved.status === 'done' ? 'followup.completed' : 'followup.updated', itemId, ['follow_up']);
    return saved;
    }));
  },

  async listContributions(clientId) {
    assertDemoClient(clientId);
    return contributionStore().filter((item) => item.clientId === clientId)
      .sort(compareContributionsCanonical);
  },

  async createContribution(input: CreateContributionInput, idempotencyKey: string) {
    return withDemoOperationLock(`contributions:${input.clientId}`, async () => {
    return withDemoStorageTransaction([CONTRIBUTION_LEDGER_KEY, CONTRIBUTIONS_KEY, CONTRIBUTION_IDEMPOTENCY_KEY, AUDIT_KEY], async () => {
    const parsed = createContributionSchema.parse(input);
    assertDemoClient(parsed.clientId);
    const key = requireDemoContributionKey(idempotencyKey);
    const requestHash = await sha256({ kind: 'create', clientId: parsed.clientId, input: parsed });
    const idempotency = contributionIdempotencyStore();
    const existingKey = idempotency.find((entry) => entry.key === key);
    if (existingKey) {
      if (existingKey.kind !== 'create' || existingKey.clientId !== parsed.clientId || existingKey.requestHash !== requestHash) throw new Error('Idempotency-Key já usada com payload diferente');
      const existing = contributionStore().find((item) => item.id === existingKey.contributionId);
      if (!existing) throw new Error('Registro de idempotência de aporte sem resultado');
      return createContributionReceipt(existing);
    }
    if (contributionStore().filter((item) => item.clientId === parsed.clientId).length >= MAX_CONTRIBUTIONS_PER_CLIENT) throw new Error('O cliente excede o limite operacional de 5.000 aportes');
    if (parsed.goalId && !(goalStore()[parsed.clientId] ?? []).some((goal) => goal.id === parsed.goalId && goal.status === 'active')) throw new Error('Meta ativa deste cliente não encontrada');
    const now = new Date().toISOString();
    const item: ClientContribution = {
      id: crypto.randomUUID(), clientId: parsed.clientId, goalId: parsed.goalId, resource: parsed.resource.trim(),
      dueDate: parsed.dueDate, plannedAmount: moneyFromNumber(parsed.plannedAmount), actualAmount: null,
      status: 'pending', realizedAt: null, note: parsed.note, settlementNote: null, version: 1, createdAt: now, updatedAt: now,
    };
    const ledger = contributionLedger();
    ledger.contributions = [...ledger.contributions, item];
    ledger.idempotency = [...idempotency, { key, kind: 'create', clientId: parsed.clientId, contributionId: item.id, requestHash }];
    saveContributionLedger(ledger);
    appendAudit(parsed.clientId, 'contribution.planned', item.id);
    return item;
    });
    }, { requireCrossContext: true });
  },

  async reconcileContributionByKey(clientId: string, idempotencyKey: string, kind: 'plan' | 'settle' = 'plan') {
    assertDemoClient(clientId);
    const key = requireDemoContributionKey(idempotencyKey);
    const existingKey = contributionIdempotencyStore().find((entry) => entry.key === key && entry.clientId === clientId);
    if (!existingKey) throw new Error('Ainda não há confirmação autoritativa para esta chave');
    if ((kind === 'plan' && existingKey.kind !== 'create') || (kind === 'settle' && existingKey.kind !== 'settle')) throw new Error('Tipo de operação incompatível com a chave');
    const existing = contributionStore().find((item) => item.id === existingKey.contributionId);
    if (!existing) throw new Error('Registro de idempotência de aporte sem resultado');
    return kind === 'plan' ? createContributionReceipt(existing) : existing;
  },

  async settleContribution(clientId, contributionId, input: SettleContributionInput, expectedVersion, idempotencyKey: string) {
    return withDemoOperationLock(`contributions:${clientId}`, async () => {
    return withDemoStorageTransaction([CONTRIBUTION_LEDGER_KEY, CONTRIBUTIONS_KEY, CONTRIBUTION_IDEMPOTENCY_KEY, AUDIT_KEY], async () => {
    assertDemoClient(clientId);
    const parsed = settleContributionSchema.parse(input);
    const key = requireDemoContributionKey(idempotencyKey);
    const requestHash = await sha256({ kind: 'settle', clientId, contributionId, expectedVersion, input: parsed });
    const idempotency = contributionIdempotencyStore();
    const existingKey = idempotency.find((entry) => entry.key === key);
    if (existingKey) {
      if (existingKey.kind !== 'settle' || existingKey.clientId !== clientId || existingKey.contributionId !== contributionId || existingKey.requestHash !== requestHash) throw new Error('Idempotency-Key já usada com payload diferente');
      const existing = contributionStore().find((item) => item.id === existingKey.contributionId);
      if (!existing) throw new Error('Registro de idempotência de aporte sem resultado');
      return existing;
    }
    const all = contributionStore();
    const index = all.findIndex((item) => item.clientId === clientId && item.id === contributionId);
    const previous = all[index];
    if (!previous) throw new Error('Aporte não encontrado');
    if (previous.version !== expectedVersion) throw new Error('Conflito de versão');
    if (previous.status !== 'pending') throw new Error('Aporte já encerrado; não é possível alterar a realização');
    let actualAmount: string | null = null;
    let realizedAt: string | null = null;
    if (parsed.status === 'realized') {
      if (parsed.actualAmount === undefined || parsed.realizedAt === undefined) throw new Error('Resultado realizado exige valor e data');
      const today = canonicalToday();
      const createdDate = canonicalDate(previous.createdAt);
      if (parsed.realizedAt < createdDate || parsed.realizedAt > today) throw new Error('A data de realização deve estar entre a criação e hoje');
      actualAmount = moneyFromNumber(parsed.actualAmount);
      realizedAt = parsed.realizedAt;
    }
    const item: ClientContribution = { ...previous, status: parsed.status,
      actualAmount, realizedAt, settlementNote: parsed.note,
      version: previous.version + 1, updatedAt: new Date().toISOString(),
    };
    all[index] = item;
    const ledger = contributionLedger();
    ledger.contributions = all;
    ledger.idempotency = [...idempotency, { key, kind: 'settle', clientId, contributionId, requestHash }];
    saveContributionLedger(ledger);
    appendAudit(clientId, parsed.status === 'realized' ? 'contribution.realized' : 'contribution.not_realized', contributionId);
    return item;
    });
    }, { requireCrossContext: true });
  },

  async listReportSnapshots(clientId) {
    if (clientId) assertDemoClient(clientId);
    return (await verifiedReports()).filter((report) => !clientId || report.clientId === clientId)
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
  },

  async listLegacyReportSnapshots(clientId) {
    if (clientId) assertDemoClient(clientId);
    return legacyReportStore().filter((report) => !clientId || report.clientId === clientId)
      .sort((left, right) => (right.createdAt ?? '').localeCompare(left.createdAt ?? ''));
  },

  async createReportSnapshot(input: CreateReportSnapshotInput) {
    return withDemoOperationLock(`report:${input.sourceCalculationId ?? `${input.clientId}:${input.scenarioId}`}`, async () => {
    const validated = createReportSnapshotSchema.parse(input);
    if (validated.sourceCalculationId && !validated.sourceRevisionFingerprint) throw new Error('Relatório-fonte exige fingerprint das revisões financeiras');
    const session = this.getSession();
    assertDemoClient(validated.clientId);
    const client = clients().find((item) => item.id === validated.clientId);
    if (!client) throw new Error('Cliente do relatório não encontrado');
    assertDemoClientScenario(validated.clientId, validated.scenarioId);
    const scenario = scenariosForClient(validated.clientId).find((item) => item.id === validated.scenarioId);
    if (!scenario) throw new Error('Cenário do relatório não encontrado');
    let authoritativeSummary = validated.summary;
    const existingReports = await verifiedReports();
    if (validated.sourceCalculationId || validated.goalFunding) {
      const activeGoals = (goalStore()[validated.clientId] ?? []).filter((goal) => goal.status === 'active' && (goal.scenarioId === null || goal.scenarioId === validated.scenarioId));
      const expectedFunding = activeGoals.map((goal) => ({ goal, amount: calculateGoalFunding(goal, scenario.assumptions).periodicContributionDue }));
      const expectedByPeriodicity = {
        monthly: sumMoney(expectedFunding.filter(({ goal }) => goal.periodicity === 'monthly').map(({ amount }) => amount)),
        annual: sumMoney(expectedFunding.filter(({ goal }) => goal.periodicity === 'annual').map(({ amount }) => amount)),
        oneTime: sumMoney(expectedFunding.filter(({ goal }) => goal.periodicity === 'one-time').map(({ amount }) => amount)),
      };
      const fundingBasis = new Set(activeGoals.map((goal) => goal.periodicity)).size > 1 ? 'mixed-periodicity' : 'single-periodicity';
      const expectedTotalGoalFunding = fundingBasis === 'single-periodicity' ? sumMoney(Object.values(expectedByPeriodicity)) : undefined;
      if (validated.summary.activeGoals !== activeGoals.length || validated.summary.totalGoalValue !== sumMoney(activeGoals.map((goal) => goal.targetValue)) ||
        validated.summary.monthlyGoalFunding !== expectedByPeriodicity.monthly || validated.summary.annualGoalFunding !== expectedByPeriodicity.annual ||
        validated.summary.oneTimeGoalFunding !== expectedByPeriodicity.oneTime || validated.summary.totalGoalFunding !== expectedTotalGoalFunding ||
        (fundingBasis === 'mixed-periodicity' && validated.summary.fundingBasis !== fundingBasis) ||
        (validated.summary.fundingBasis !== undefined && validated.summary.fundingBasis !== fundingBasis)) throw new Error('Resumo de metas não corresponde ao cenário');
      const seen = new Set<string>();
      for (const item of validated.goalFunding ?? []) {
        const goal = activeGoals.find((candidate) => candidate.id === item.goalId);
        const expected = expectedFunding.find(({ goal: candidate }) => candidate.id === item.goalId);
        if (!goal || !expected || seen.has(item.goalId) || goal.title !== item.title || goal.category !== item.category || goal.periodicity !== item.periodicity || expected.amount !== item.amount) throw new Error('Funding de relatório não corresponde às metas');
        seen.add(item.goalId);
      }
      if (seen.size !== activeGoals.length) throw new Error('Funding de relatório não cobre todas as metas');
    }
    if (validated.sourceCalculationId) {
      const activeGoals = (goalStore()[validated.clientId] ?? []).filter((goal) => goal.status === 'active' && (goal.scenarioId === null || goal.scenarioId === validated.scenarioId));
      const exclusionSelections = exclusionStore()[validated.clientId] ?? [];
      const excludedExpenseIds = new Set(resolveExcludedTargetIds(exclusionSelections, validated.scenarioId, 'expense'));
      const currentFinancials = financialStore().filter((entry) => entry.clientId === validated.clientId && entry.active && (entry.kind !== 'expense' || !excludedExpenseIds.has(entry.id)));
      const currentGuaranteeExposures = guaranteeExposureStore().filter((exposure) => exposure.clientId === validated.clientId);
      const currentProtection = protectionStore()[validated.clientId] ?? null;
      const familyMembers = [...(familyStore()[validated.clientId] ?? [])].sort((left, right) => left.id.localeCompare(right.id));
      const family = familyMembers.map((member) => ({ id: member.id, version: member.version })).sort((left, right) => left.id.localeCompare(right.id));
      if (currentProtection) {
        const excludedFamilyIds = new Set(resolveExcludedTargetIds(exclusionSelections, null, 'family_member'));
        const protectionFamily = familyMembers.filter((member) => !excludedFamilyIds.has(member.id)).map((member) => ({ id: member.id, version: member.version })).sort((left, right) => left.id.localeCompare(right.id));
        const protectionGuarantees = effectiveGuaranteeExposures(currentGuaranteeExposures, currentProtection.guaranteesScenarioId, exclusionSelections);
        const currentProtectionFingerprint = await sha256(protectionRevisionPayload({ clientId: validated.clientId, family: protectionFamily, guaranteesMode: currentProtection.guaranteesMode, guaranteesScenarioId: currentProtection.guaranteesScenarioId, guaranteeExposures: protectionGuarantees, exclusions: exclusionSelections }));
        const legacyProtectionFingerprint = await sha256({ clientId: validated.clientId, family });
        const acceptsLegacy = exclusionSelections.length === 0;
        if (!currentProtection.sourceRevisionFingerprint || (currentProtection.sourceRevisionFingerprint !== currentProtectionFingerprint && (!acceptsLegacy || currentProtection.sourceRevisionFingerprint !== legacyProtectionFingerprint))) throw new Error('O diagnóstico de proteção está desatualizado em relação à Família ou às desconsiderações');
      }
      const currentPensions = pensionStore()[validated.clientId] ?? [];
      const currentFinancings = financingStore()[validated.clientId] ?? [];
      const currentComparisons = comparisonStore()[validated.clientId] ?? [];
      const currentContributions = contributionLedger().contributions.filter((item) => item.clientId === validated.clientId).sort(compareContributionsCanonical);
      const currentFollowUpItems = followUpStore().filter((item) => item.clientId === validated.clientId);
      const currentPipeline = pipelineStore()[validated.clientId] ?? { clientId: validated.clientId, stage: 'new' as const, version: 1, history: [] };
      const expectedFingerprint = await reportSourceRevisionDigest({
        scenario,
        goals: activeGoals,
        financials: currentFinancials,
        guaranteeExposures: currentGuaranteeExposures,
        protection: currentProtection,
        pensions: currentPensions,
        financings: currentFinancings,
        financingComparisons: currentComparisons,
        contributions: currentContributions,
        followUp: currentFollowUpItems,
        pipelineVersion: currentPipeline.version,
        family,
        exclusions: exclusionSelections,
      });
      if (validated.sourceRevisionFingerprint !== expectedFingerprint) throw new Error('As fontes financeiras mudaram ou o fingerprint não foi calculado pela autoridade atual');
      const effectiveCurrentGuarantees = effectiveGuaranteeExposures(currentGuaranteeExposures, validated.scenarioId, exclusionSelections);
      const expectedDetails = {
        goals: activeGoals.map((goal) => ({ id: goal.id, title: goal.title, targetValue: goal.targetValue, reservedValue: goal.reservedValue, periodicity: goal.periodicity, status: goal.status })),
        financials: currentFinancials.map((entry) => ({ id: entry.id, title: entry.title, kind: entry.kind, institution: entry.institution, currentValue: entry.currentValue, monthlyValue: entry.monthlyValue, retirementMonthlyValue: entry.retirementMonthlyValue, liquidity: entry.liquidity, periodicity: entry.periodicity })),
        guarantees: effectiveCurrentGuarantees.map(({ id, kind, label, value }) => ({ id, kind, label, value })),
        protection: currentProtection ? { dependents: currentProtection.dependents, totalExpenses: currentProtection.totalExpenses, emergencyReserve: currentProtection.emergencyReserve, successionNeed: currentProtection.successionNeed, recommendedCapital: currentProtection.recommendedCapital, coverageGap: currentProtection.coverageGap } : null,
        pensions: currentPensions.map((item) => ({ id: item.id, name: item.name, product: item.product, currentReserve: item.currentReserve, monthlyContribution: item.monthlyContribution, grossReserve: item.result.grossReserve, netReserve: item.result.netReserve, sustainableMonthlyIncome: item.result.sustainableMonthlyIncome })),
        financings: currentFinancings.map((item) => ({ id: item.id, name: item.name, kind: item.kind, principal: item.principal, firstPayment: item.firstPayment, lastPayment: item.lastPayment, totalPaid: item.totalPaid, termMonths: item.termMonths, schedule: item.schedule })),
        financingComparisons: [...currentComparisons].sort((left, right) => left.id.localeCompare(right.id)),
        contributions: currentContributions.map((item) => ({ id: item.id, resource: item.resource, dueDate: item.dueDate, plannedAmount: item.plannedAmount, actualAmount: item.actualAmount, status: item.status, realizedAt: item.realizedAt })),
        followUp: { items: [...currentFollowUpItems].sort((left, right) => left.id.localeCompare(right.id)), pipeline: currentPipeline },
        family: familyMembers.map(({ id, fullName, relationship, birthDate, financiallyDependent, successionDependent, financialDependencyEnd, protectionNeeded, version }) => ({ id, fullName, relationship, birthDate, financiallyDependent, successionDependent, financialDependencyEnd, protectionNeeded, version })),
      };
      if (!validated.details || canonicalJson(validated.details) !== canonicalJson(expectedDetails)) throw new Error('Os detalhes financeiros do relatório não correspondem às fontes persistidas atuais');
      const source = (await verifiedCalculations()).find((item) => item.id === validated.sourceCalculationId);
      if (!source || source.clientId !== validated.clientId || source.scenarioId !== validated.scenarioId) throw new Error('Cálculo-fonte do relatório não corresponde ao cliente/cenário');
      if (canonicalJson(source.input) !== canonicalJson(scenario.assumptions)) throw new Error('Cálculo-fonte do relatório está desatualizado em relação às premissas do cenário');
      const expectedSummary = reportSummaryForClient(
        { version: 1, scenarios: [scenario], goals: activeGoals, financials: currentFinancials, guaranteeExposures: currentGuaranteeExposures, protection: currentProtection, exclusions: exclusionSelections, r5: { version: 1, pensionSimulations: currentPensions, financingSimulations: currentFinancings, financingComparisons: currentComparisons } },
        { version: 1, items: currentFollowUpItems, pipeline: currentPipeline, contributions: currentContributions, contributionOperations: [] },
        validated.scenarioId,
        source.output,
        currentContributions,
      );
      for (const [key, value] of Object.entries(validated.summary)) {
        if (value !== undefined && value !== expectedSummary[key as keyof typeof expectedSummary]) throw new Error('Resumo do relatório não corresponde às fontes financeiras atuais');
      }
      authoritativeSummary = expectedSummary;
    }
    if (validated.sourceCalculationId) {
      const existing = existingReports.find((report) => report.sourceCalculationId === validated.sourceCalculationId && report.clientId === validated.clientId && report.scenarioId === validated.scenarioId);
      if (existing) {
        if (existing.kind !== validated.kind || existing.title !== validated.title.trim() || existing.sourceRevisionFingerprint !== validated.sourceRevisionFingerprint || canonicalJson(existing.summary) !== canonicalJson(validated.summary) || canonicalJson(existing.goalFunding ?? []) !== canonicalJson(validated.goalFunding ?? []) || canonicalJson(existing.details ?? null) !== canonicalJson(validated.details ?? null) || canonicalJson(existing.sections ?? []) !== canonicalJson(validated.sections ?? [])) throw new Error('A chave do cálculo já foi usada com conteúdo de relatório diferente');
        return existing;
      }
    }
    const base = {
      id: crypto.randomUUID(), clientId: validated.clientId, scenarioId: validated.scenarioId,
      kind: validated.kind, title: validated.title.trim(), sourceVersion: scenario.version, summary: authoritativeSummary,
      createdAt: new Date().toISOString(), createdBy: session?.id ?? 'demo-anonymous', canonical: false,
      sourceCalculationId: validated.sourceCalculationId,
      sourceRevisionFingerprint: validated.sourceRevisionFingerprint,
      ...(validated.goalFunding ? { goalFunding: validated.goalFunding } : {}),
      details: validated.details,
      ...(validated.sections ? { sections: validated.sections.map((section, position) => ({ ...section, position })) } : {}),
    };
    const snapshot: ReportSnapshot = {
      ...base,
      sha256: await sha256(base),
    };
    reportSnapshotSchema.parse(snapshot);
    saveReportStore([...existingReports, snapshot]);
    return snapshot;
    });
  },

  async calculateRetirement(request: RetirementCalculationRequest) {
    const validated = retirementCalculationRequestSchema.parse(request);
    const canonicalKey = validated.idempotencyKey.trim().toLowerCase();
    return withDemoOperationLock(`calculation:${canonicalKey}`, async () => {
    assertDemoClientScenario(validated.clientId, validated.scenarioId);
    const scenario = scenariosForClient(validated.clientId).find((item) => item.id === validated.scenarioId);
    if (!scenario) throw new Error('Cenário não pertence ao cliente');
    if (scenario.version !== validated.expectedScenarioVersion) throw new Error('Conflito de versão do cenário');
    if (scenario.assumptionsVersion !== validated.expectedAssumptionsVersion) throw new Error('Conflito de versão das premissas');
    if (canonicalJson(scenario.assumptions) !== canonicalJson(validated.input)) throw new Error('As premissas calculadas não correspondem ao cenário persistido');
    const all = await verifiedCalculations();
    const existing = all.find((item) => item.idempotencyKey?.trim().toLowerCase() === canonicalKey);
    if (existing) {
      if (existing.clientId !== validated.clientId || existing.scenarioId !== validated.scenarioId || canonicalJson(existing.input) !== canonicalJson(validated.input) || existing.sha256 !== (await sha256({ clientId: existing.clientId, scenarioId: existing.scenarioId, engineVersion: existing.engineVersion, input: existing.input, output: existing.output }))) throw new Error('Idempotency-Key já usada com payload diferente');
      return existing;
    }
    const output = serializeRetirementResult(runRetirementSimulation(validated.input));
    const payload = { clientId: validated.clientId, scenarioId: validated.scenarioId, engineVersion: ENGINE_VERSION, input: validated.input, output };
    const session = this.getSession();
    const snapshot: CalculationSnapshot = {
      id: crypto.randomUUID(),
      clientId: validated.clientId,
      scenarioId: validated.scenarioId,
      calculationRunId: crypto.randomUUID(),
      idempotencyKey: canonicalKey,
      engineVersion: ENGINE_VERSION,
      input: validated.input,
      output,
      sha256: await sha256(payload),
      createdAt: new Date().toISOString(),
      createdBy: session?.id ?? 'demo-anonymous',
      canonical: false,
    };
    if (!calculationSnapshotCalendarConsistent(snapshot.input, snapshot.output)) throw new Error('Snapshot de cálculo demo incoerente com o calendário');
    calculationSnapshotSchema.parse(snapshot);
    demoStorage.setItem(SNAPSHOTS_KEY, JSON.stringify([...all, snapshot]));
    return snapshot;
    });
  },

  async listCalculationSnapshots(clientId?: string) {
    const snapshots = await verifiedCalculations();
    return [...(clientId ? snapshots.filter((snapshot) => snapshot.clientId === clientId) : snapshots)].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
  },

  getSettings() {
    try {
      return demoSettingsSchema.parse(read<unknown>(demoStorage, SETTINGS_KEY, defaultDemoSettings));
    } catch {
      try { setDemoItem(SETTINGS_KEY, JSON.stringify(defaultDemoSettings)); } catch { /* quota não deve derrubar a tela de settings */ }
      return defaultDemoSettings;
    }
  },

  saveSettings(value) {
    const validated = demoSettingsSchema.parse(value);
    setDemoItem(SETTINGS_KEY, JSON.stringify(validated));
  },
};

export const demoRepository: FintracksRepository = asyncRepository;

const DEMO_RESET_KEYS = [
  CLIENTS_KEY, SCENARIO_KEY, SNAPSHOTS_KEY, GOALS_KEY, FOLLOW_UP_KEY, CONTRIBUTIONS_KEY,
  FINANCIALS_KEY, PROTECTION_KEY, REPORTS_KEY, LEGACY_REPORTS_KEY, PENSIONS_KEY, FINANCING_KEY, COMPARISONS_KEY,
  SETTINGS_KEY, FAMILY_KEY, FILES_KEY, FILE_BLOBS_KEY, PIPELINE_KEY, AUDIT_KEY,
  CONTRIBUTION_IDEMPOTENCY_KEY, CONTRIBUTION_LEDGER_KEY, OPERATION_RECEIPTS_KEY,
] as const;

export async function resetDemoData(): Promise<void> {
  await withDemoOperationLock('__reset__', async () => {
    const before = new Map(DEMO_RESET_KEYS.map((key) => [key, demoStorage.getItem(key)]));
    const blobsBefore = new Map(demoFileBlobs);
    try {
      for (const key of DEMO_RESET_KEYS) demoStorage.removeItem(key);
      demoFileBlobs.clear();
    } catch (error) {
      for (const [key, value] of before) {
        try {
          if (value === null) demoStorage.removeItem(key);
          else demoStorage.setItem(key, value);
        } catch { /* preserve the original reset error */ }
      }
      demoFileBlobs.clear();
      for (const [id, blob] of blobsBefore) demoFileBlobs.set(id, blob);
      throw error;
    }
  }, { requireCrossContext: false });
}

// Preferências de apresentação são locais em ambos os modos e não fazem parte do domínio financeiro.
export const localRepository = {
  getSettings: () => asyncRepository.getSettings(),
  saveSettings: (value: DemoSettings) => asyncRepository.saveSettings(value),
  resetLocalData: resetDemoData,
};
